import axios from "axios";
import MockAdapter from "axios-mock-adapter";
import config from "../config";
import fakeBackend from "../helpers/AuthType/fakeBackend";
import { registerResource, getResourceStore } from "./crudFactory";
import * as XLSX from "xlsx";
import {
  DCKEEPER_ROOM_COLUMNS, DCKEEPER_RACK_COLUMNS,
  DCKEEPER_HARDWARE_COLUMNS, DCKEEPER_SOFTWARE_COLUMNS,
} from "../helpers/importContract";
import registerApprovals, { listApprovals } from "./approvals";
import registerAuditArchive, { seedArchiveEntry } from "./auditArchive";
import { SERVICE_LEVELS, MAINTENANCE_CYCLES } from "./db/contractLookups";
import registerPasswordReset, {
  setInitialPassword, credentialsValid,
  issueRecoveryToken, recoveryTokenState, consumeRecoveryToken,
} from "./passwordReset";
import { passwordIssue } from "../helpers/passwordRules";
import * as url from "../helpers/url_helper";
// DcRack 投影要算 used_u —— 與 U 位圖同一份算法,不要在 mock 裡另寫一套
import { uUsageOf } from "../pages/DCKeeper/Assets/uSlots";

import {
  roles, users,
  userRowToDcCustomer, userRowToDcCustomerListItem, dcCustomerPatchToUserRow,
  roleRowToCustomerRole, customerRolePatchToRoleRow,
} from "./db/accounts";
import { workItems } from "./db/workItems";
import { countByResource } from "../constants/workResources";
import { decorateManager, syncManager } from "./assetManager";
import { decorateWorkItem } from "./workItemLabels";
import { rooms } from "./db/rooms";
import { cabinets, hardware } from "./db/assets";
import { notifications } from "./db/notifications";
import { notifyMuteRules } from "./db/notifyMuteRules";
import { notifyRules } from "./db/notifyRules";
import { notifyEventSettings } from "./db/notifyEvents";
import { syncNotifyFeed } from "./notifyScheduler";
import { ips, projects, contracts, vendors, software } from "./db/registry";
import { vulns } from "./db/vulns";
import { monitorDevices } from "./db/monitor";
import { findUConflict, uConflictMessage, occupancyOf } from "../pages/DCKeeper/Assets/uSlots";
import { MOCK_JWT_TOKEN } from "../constants/auth";
import { ASSET_TYPES, BIZ_TYPES } from "../helpers/assetOptions";
import { formatUPosition, parseUPosition } from "../helpers/hardwareContract";

/**
 * 建立 DC Keeper 前端 mock 層。
 *
 * 單一 MockAdapter（onNoMatch: passthrough）：已註冊的端點回 mock 資料，
 * 未註冊的（例如已切換的真後端 API）直接放行到 axios.defaults.baseURL。
 *
 * 後端 Swagger 到位後：在 .env 設 REACT_APP_USE_MOCK=false 即整層停用；
 * 或逐一移除下方對應 registerResource 讓該資源走真 API（漸進式切換）。
 */
// GetToken 與密碼找回共用的假 GUID(真後端=customer_guid;信箱連結驗證用;測試也引)
export const mockGuidOf = (id) => `8f14e45f-ceea-467f-a8ee-${String(id).padStart(12, "0")}`;

// beforeEach 每測重建一層卻不 restore 的話,同一 axios 會疊 N 個 adapter。
// HardwarePage 64 測跑到後面,請求被每一層都攔一次,fillNewHardware 就卡到 Jest timeout。
let liveAdapter = null;

export default function setupMock() {
  if (liveAdapter) {
    liveAdapter.restore();
    liveAdapter = null;
  }
  const mock = new MockAdapter(axios, { onNoMatch: "passthrough" });
  liveAdapter = mock;

  // 混合模式:已串接的真後端路徑(/api-backend/*)放行到 REACT_APP_API_URL,
  // 其餘模組(尚無後端)繼續由下方 mock 服務(背景見 ticket api-v1-integration/07)。
  // adapter 依註冊順序比對,這條要放在所有 handler 之前才攔得到;錨定前綴,
  // 避免路徑中段偶然含 /api-backend/ 的請求被誤放行。
  if (config.BACKEND_PASSTHROUGH) {
    if (config.API_URL) {
      mock.onAny(/^\/api-backend\//).passThrough();
    } else {
      // 設了 hybrid 卻沒給 API_URL:退回全 mock 可運作,但要讓設定錯誤看得見
      console.warn(
        "[mock] REACT_APP_USE_MOCK=hybrid 但未設 REACT_APP_API_URL — /api-backend/* 退回 mock 服務"
      );
    }
  }

  // 沿用模板的假登入/行事曆等既有 mock（掛在同一個 adapter 上，避免衝突）
  fakeBackend(mock);

  // —— DC Keeper 資源 ——
  // 模組01 人員管理
  registerResource(mock, {
    base: url.DCK.ROLES, name: "role",
    seed: roles,
    searchFields: ["name", "code", "description"],
    importFields: { required: ["name", "code"], unique: "code" },
    // 綁定中的使用者要先改綁才准刪(需求書:角色異動/刪除前檢查是否有綁定使用者)
    canDelete: (role) => {
      const bound = users.filter((u) => String(u.roleId) === String(role.id)).length;
      return bound > 0
        ? `角色「${role.name}」仍有 ${bound} 位綁定使用者，無法刪除。`
        : true;
    },
  });
  registerResource(mock, {
    base: url.DCK.USERS, name: "user",
    seed: users,
    searchFields: ["name", "account", "email", "department"],
    importFields: { required: ["name", "account", "email"], unique: "account" },
    // 管理者設的是「初始密碼」:明文收進 auth 那邊,不留在使用者資料列上;
    // 帳號帶著標記,首次登入會被要求變更(ticket 34)
    beforeCreate: (record, payload) => {
      setInitialPassword(record.id, payload.password);
      delete record.password;
      record.mustChangePassword = true;
      return null;
    },
    // 需求書 §2.2.3「執行或管理工作是否都已解除」:名下還有負責關係就擋。
    // 只回「不能刪」等於把人丟在原地,所以訊息要說得出卡在哪幾類、各幾筆,以及怎麼解除。
    canDelete: (u) => {
      // work-item 在同一次 setupMock 裡就註冊了,一定拿得到;
      // 回頭讀 db 的 seed 會看不到 run-time 新增的關係。
      const mine = getResourceStore("work-item").all().filter((w) => w.userId === u.id);
      if (mine.length === 0) return true;
      const detail = countByResource(mine).map((r) => `${r.label} ${r.count}`).join("、");
      return (
        `「${u.name}」名下尚有負責工作（${detail}，共 ${mine.length} 筆），無法刪除。\n` +
        `請到「人員管理 › 工作管理」把這些工作轉派給其他人，核准後即可刪除。`
      );
    },
  });
  // 工作項目＝人×資產的負責關係(ticket 31)。沒有可模糊搜尋的文字欄位,
  // 查詢一律用等值條件(例如 ?userId=2)。
  registerResource(mock, {
    base: url.DCK.WORK_ITEMS, name: "work-item",
    seed: workItems,
    // 卡片要顯示的名稱與辨識欄位在被指向的資產上,一起算出來回給前端
    decorate: decorateWorkItem,
  });

  // 模組03 機房管理（刪除阻擋：仍有機櫃時不可刪）
  registerResource(mock, {
    base: url.DCK.ROOMS, name: "room",
    seed: rooms,
    searchFields: ["name", "code", "city"],
    importFields: { required: ["name", "code"], unique: "code" },
    canDelete: (room) =>
      room.cabinetCount > 0
        ? `機房「${room.name}」仍有 ${room.cabinetCount} 個機櫃，請先解除機櫃綁定再刪除。`
        : true,
  });

  // 模組05 資產管理（刪除阻擋：機櫃仍有硬體時不可刪）
  registerResource(mock, {
    base: url.DCK.CABINETS, name: "cabinet",
    seed: cabinets,
    // manager 是 decorate 出來的欄位(工作項目回填);查詢框說「編號、廠牌或管理者」,要搜得到
    searchFields: ["name", "code", "docNo", "brand", "model", "manager"],
    importFields: { required: ["code", "roomId"], unique: "code" },
    // 管理者不存在機櫃資料上,讀的時候才從負責關係算出來(ticket 35);
    // 機房/樓層/區域名稱也是讀取時由 id 對照(票 02:IP 關聯查詢要顯示位置,
    // 衍生值在 mock 算、前端不推導,ADR-0007)
    decorate: (row) => {
      const room = rooms.find((r) => r.id === row.roomId);
      const floor = room?.floors?.find((f) => f.id === row.floorId);
      const area = floor?.areas?.find((a) => a.id === row.areaId);
      return {
        ...decorateManager("cabinet")(row),
        roomName: room?.name || "",
        floorName: floor?.name || "",
        areaName: area?.name || "",
      };
    },
    beforeCreate: syncManager("cabinet"),
    beforeUpdate: syncManager("cabinet"),
    canDelete: (cab, store) => {
      // 註：此處僅示意；跨資源檢查正式應由後端處理
      // 讀 live store 不讀 seed —— run-time 新增/刪除的硬體只存在 store 裡
      const hwStore = getResourceStore("hardware");
      const inCabinet = (hwStore ? hwStore.all() : hardware).filter((h) => String(h.cabinetId) === String(cab.id));
      const reserved = inCabinet.filter((h) => occupancyOf(h) === "reserved");
      // 兩種阻擋一次講完:分兩次講的話,使用者清掉上架的機器才發現還有保留位,
      // 白跑一趟(兩段式新增 ticket 05)
      const reasons = [];
      if (inCabinet.some((h) => occupancyOf(h) === "occupied")) {
        reasons.push("仍有硬體上架，請先下架");
      }
      // 保留位的對面沒有機器,叫人「先下架」他會找不到東西可下架 ——
      // 要說的是哪張單把位置留住了,以及怎麼解
      if (reserved.length) {
        const docs = reserved.map((h) => h.docNo).filter(Boolean).join("、");
        reasons.push(
          `還有 ${reserved.length} 筆已核准待上架的保留位${docs ? `（單據 ${docs}）` : ""}，`
          + "請先完成上架或撤回該單據"
        );
      }
      return reasons.length ? `機櫃「${cab.name}」${reasons.join("；")}再刪除。` : true;
    },
  });
  registerResource(mock, {
    base: url.DCK.HARDWARE, name: "hardware",
    seed: hardware,
    searchFields: ["code", "name", "brand", "model", "serialNo", "ip"],
    importFields: {
      required: ["code", "cabinetId", "uPosition"],
      unique: "code",
      // U 位衝突(ticket 29):逐列比對既有硬體 + 同批「前面已經過關」的列,
      // 三種情境(與既有硬體衝突／同批列互衝／超出機櫃總U數)都靠 findUConflict 判斷,
      // 跟表單即時防呆(HardwareForm)共用同一支邏輯,不會兩邊講不同的規則。
      // 注意:用 crudFactory 傳入的 store(即時資料)比對,不是 import 進來的種子陣列 ——
      // 種子在執行期不會跟著上下架異動更新,用它比對會漏掉/誤判剛異動過的硬體。
      validateBatch: (rows, hardwareStore) => {
        const failed = [];
        const cabinetStore = getResourceStore("cabinet");
        const accepted = []; // 沒衝突才視為「這批已確定要上架」,後面的列才會跟它比對
        rows.forEach((row, index) => {
          const cabinet = cabinetStore ? cabinetStore.find(row.cabinetId) : null;
          const conflict = findUConflict({
            cabinet, hardware: [...hardwareStore, ...accepted],
            uPosition: row.uPosition, uSize: row.uSize,
          });
          if (conflict) {
            failed.push({ index, field: "uPosition", reason: uConflictMessage(conflict) });
          } else {
            accepted.push(row);
          }
        });
        return failed;
      },
    },
    decorate: decorateManager("hardware"),
    beforeCreate: syncManager("hardware"),
    beforeUpdate: syncManager("hardware"),
  });

  // 模組07 訊息功能（查詢頁 feed,含 EOS/EOL/CVE/KEV 主機通知;依 Figma DCK-07-01-02）
  // 每次列表前先讓排程器把「規則×情境」的最新通知實體化進來(ticket 11):
  // 規則核准/暫停的效果,下一次開 feed 就看得到
  registerResource(mock, {
    base: url.DCK.NOTIFICATIONS, name: "notification",
    seed: notifications,
    searchFields: ["source", "type", "message", "docNo"],
    beforeList: () => syncNotifyFeed(),
  });
  // 暫停規則(來源模組 × 型別;ticket 13)——建立/解除都走審核,active 由審核引擎回寫
  registerResource(mock, {
    base: url.DCK.NOTIFY_MUTE_RULES, name: "notify-mute-rule",
    seed: notifyMuteRules,
    searchFields: ["source", "type"],
  });
  // 通知規則(各類訊息設定;9月交付 ticket 09)——暫停/啟用/刪除全走審核,
  // active 由審核引擎的 rule-pause／rule-resume 政策回寫
  registerResource(mock, {
    base: url.DCK.NOTIFY_RULES, name: "notify-rule",
    seed: notifyRules,
    searchFields: ["name", "category"],
  });
  // 系統事件通知設定(單一設定文件;僅接收管道可調,見種子註解)
  registerResource(mock, {
    base: url.DCK.NOTIFY_EVENT_SETTINGS, name: "notify-event-setting",
    seed: notifyEventSettings,
  });

  // 模組04 營運資訊 + 05-03 軟體(Sprint 2 空殼頁資料層;API 到位時替換即可)
  // IP 資訊:頁面已切真後端 DcIpAddress(票 9月交付/16,handler 在下方「DcIpAddress」段)。
  // 這裡保留 registerResource 只為了**登記 "ip" store**(審核引擎回寫用;/api/ips 不再有頁面打,
  // 匯入也沒了 —— DcIpAddress 沒有匯入端點);`assets[]` 是 {id, assetType, assetId} 參照(見 db/registry.js 註解)。
  registerResource(mock, {
    base: url.DCK.IPS, name: "ip",
    seed: ips,
    searchFields: ["ip", "mac", "iface"],
    canDelete: (row) =>
      (row.assets || []).length > 0
        ? `IP「${row.ip}」仍關聯 ${row.assets.length} 筆資產，請先於編輯頁移除關聯再刪除。`
        : true,
  });
  registerResource(mock, {
    base: url.DCK.PROJECTS, name: "project", seed: projects, searchFields: ["name", "owner"],
    importFields: { required: ["name"], unique: "name" },
  });
  registerResource(mock, {
    base: url.DCK.CONTRACTS, name: "contract",
    seed: contracts,
    // 稿的搜尋框:「搜尋合約編號、合約名稱、廠商」(票 04)
    searchFields: ["code", "name", "vendorName"],
    importFields: { required: ["name", "endDate"], unique: "name" },
    // 被別份合約列為上案/關聯的合約不能說刪就刪(票 04:刪除被關聯者要阻擋;票 33 起關聯是 relations 參照)
    canDelete: (row, store) => {
      const refs = (store || []).filter((c) =>
        String(c.id) !== String(row.id) &&
        (String(c.mainContractId) === String(row.id) ||
          (c.relations || []).some((r) => String(r.relatedId) === String(row.id)))
      ).length;
      return refs > 0
        ? `合約「${row.name}」仍被 ${refs} 份合約列為主約或關聯合約，請先解除關聯再刪除。`
        : true;
    },
  });
  registerResource(mock, {
    base: url.DCK.VENDORS, name: "vendor", seed: vendors, searchFields: ["name", "type", "contact"],
    canDelete: (row) => {
      const store = getResourceStore("contract");
      const bound = (store ? store.all() : []).filter((c) => c.vendorName === row.name).length;
      return bound > 0
        ? `廠商「${row.name}」仍有 ${bound} 份合約，請先處理合約再刪除。`
        : true;
    },
  });
  registerResource(mock, { base: url.DCK.SOFTWARE, name: "software", seed: software, searchFields: ["name", "version", "deviceCode"] });

  // CVE/KEV 弱點(智慧看板明細;正式=後端爬蟲)
  // /api/vulns 已於票 29 汰換(改走 AssetVersion/GetVulnerabilities,見下方投影)。
  // 種子 vulns 留著當投影的資料源,但不再對外開一支 REST 資源 ——
  // 兩個資料源同時活著,查起來會分不清畫面上的數字是哪一邊來的。

  // 監控設備(DCK-06;正式=WhatsUp Gold 介接,mock 先以資源端點模擬,唯讀使用)
  registerResource(mock, {
    base: url.DCK.MONITOR_DEVICES, name: "monitorDevice", seed: monitorDevices,
    searchFields: ["hostname", "ip", "code"],
  });

  // ═══════════ 真後端(/api-backend/*)mock 版 — Backend v1.00 契約形狀 ═══════════
  // 全域 security:未帶 Bearer token 一律 401(Authenticate/GetToken 除外)
  const missingBearer = (config) =>
    !/^Bearer .+/.test((config.headers && config.headers.Authorization) || "");

  // /api-backend/* 端點的 Bearer 防線:包住 handler,未帶 token 一律 401
  // (與真後端全域 security 一致;GetToken 與 Recovery 匿名端點不包)
  const withAuth = (handler) => (config) =>
    missingBearer(config) ? [401, ""] : handler(config);

  // 後端契約分頁信封
  /**
   * ⚠️ mock-only 契約外的 `status` 查詢參數(WF_v3 票 21「狀態可篩選」;
   * 真後端各 GetAll 尚無對應 query,已列回報後端)。
   * 語意四支(room/rack/hardware/software)共用一份,別各寫各的:
   * 「審核中」看送審狀態;其餘比對資料狀態,且排除送審中的列(畫面上那些列
   * 顯示的是「審核中」,不該同時出現在資料狀態的篩選結果裡)。
   */
  const filterByStatus = (rows, rawStatus, { defaultStatus = "" } = {}) => {
    const status = String(rawStatus || "").trim();
    if (!status) return rows;
    return status === "審核中"
      ? rows.filter((r) => r.approvalStatus === "pending")
      : rows.filter((r) => (r.status || defaultStatus) === status && r.approvalStatus !== "pending");
  };

  const toPageEnvelope = (list, pageIndex, pageSize) => ({
    total_count: list.length,
    total_pages: Math.max(1, Math.ceil(list.length / pageSize)),
    has_previous_page: pageIndex > 0,
    has_next_page: (pageIndex + 1) * pageSize < list.length,
    items: list.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize),
    page_index: pageIndex,
    page_size: pageSize,
  });

  /**
   * 陣列查詢參數的正規化。
   *
   * axios 送 `riskTypes=EOS&riskTypes=CVE` 時,axios-mock-adapter 給的 `params`
   * 是原本傳進去的值 —— 可能是陣列,也可能只有一個元素時被寫成純量。
   * 兩種都要收,否則「只選一個條件」會在 mock 裡靜靜地失效。
   */
  const asArray = (v) => (v === undefined || v === null || v === "" ? [] : (Array.isArray(v) ? v : [v]));

  // ── CheckUnique mock（UAT #7）：欄位唯一性即時檢查 ──
  // 200 = 值可用；409 = 已存在。field/value/excludeId 三參數對齊後端契約。
  // DTO 欄位名 → store 欄位名的對照（只有不一致的才列）
  const UNIQUE_FIELD_MAP = { employee_no: "employeeId" };
  const makeCheckUnique = (storeName) => withAuth((config) => {
    const p = config.params || {};
    const storeField = UNIQUE_FIELD_MAP[p.field] || p.field;
    const value = String(p.value || "").trim().toLowerCase();
    const excludeId = p.excludeId != null ? Number(p.excludeId) : null;
    if (!storeField || !value) return [400, "field 與 value 為必填"];
    const store = getResourceStore(storeName);
    if (!store) return [200]; // store 不存在 → 放行
    const dup = store.all().find((r) =>
      String(r[storeField] || "").trim().toLowerCase() === value &&
      (excludeId == null || r.id !== excludeId)
    );
    return dup ? [409, { message: "此值已存在" }] : [200];
  });
  // 五張表：DcCustomer / DcRoom / DcRack / DcHardware / DcSoftware
  mock.onGet(new RegExp(`${url.DCK_BACKEND.CUSTOMER}/CheckUnique`)).reply(makeCheckUnique("user"));
  mock.onGet(new RegExp(`${url.DCK_BACKEND.ROOM}/CheckUnique`)).reply(makeCheckUnique("room"));
  mock.onGet(new RegExp(`${url.DCK_BACKEND.RACK}/CheckUnique`)).reply(makeCheckUnique("cabinet"));
  mock.onGet(new RegExp(`${url.DCK_BACKEND.HARDWARE}/CheckUnique`)).reply(makeCheckUnique("hardware"));
  mock.onGet(new RegExp(`${url.DCK_BACKEND.SOFTWARE}/CheckUnique`)).reply(makeCheckUnique("software"));

  const userStore = () => getResourceStore("user");

  // 當前使用者(真後端由 JWT 識別;mock 以 session 的 authUser 對應)。
  // 「記住我」未勾時 session 住 sessionStorage,兩邊都要認得
  const authUserRaw = () => {
    try {
      return sessionStorage.getItem("authUser") || localStorage.getItem("authUser");
    } catch (e) {
      return null;
    }
  };
  const currentUserRow = () => {
    try {
      const raw = authUserRaw();
      const id = raw ? JSON.parse(raw).id : null;
      return id != null ? userStore().find(Number(id)) : null;
    } catch (e) {
      return null;
    }
  };

  // Authenticate/GetToken:200 → AuthenticateResponse;400/401 → string(與文件一致)。
  // 行為與 mock auth/login 同一套規則:帳號(account/email)查得到且密碼驗證通過
  // 才發 token(種子帳號無初始密碼紀錄 → 不驗;管理者新增的帳號要用初始密碼);
  // 帳號不存在與密碼錯誤同樣回 401,不透露帳號是否存在。
  mock.onPost(url.DCK_BACKEND.AUTH_GET_TOKEN).reply((config) => {
    const { username, password } = JSON.parse(config.data || "{}");
    if (!username || !password) return [400, "使用者名稱與密碼為必填"];
    const key = String(username).trim().toLowerCase();
    const hit = userStore().all().find(
      (u) =>
        (u.email || "").toLowerCase() === key ||
        (u.account || "").toLowerCase() === key
    );
    if (!hit || !credentialsValid(hit.id, password)) {
      return [401, "Invalid username or password"];
    }
    return [200, {
      username: hit.account || username,
      customer_id: hit.id,
      customer_guid: mockGuidOf(hit.id),
      token: MOCK_JWT_TOKEN,
    }];
  });

  // DcCustomerRole(DCK-01-01,ticket 11):與 /api/roles 共用 role store,
  // 「同一份資料、兩種契約視角」。GetAll query: keywords/showHidden/pageIndex/pageSize。
  const roleStore = () => getResourceStore("role");

  mock.onGet(`${url.DCK_BACKEND.CUSTOMER_ROLE}/GetAll`).reply(withAuth((config) => {
    const p = config.params || {};
    const keywords = String(p.keywords || "").trim();
    const showHidden = p.showHidden === true || p.showHidden === "true";
    const pageIndex = Number(p.pageIndex) || 0;
    const pageSize = Number(p.pageSize) || 2147483647;
    // ⚠️ mock-only 契約外參數 status(WF_v3 票 29 狀態篩選;真後端 GetAll 尚無,已列回報後端)。
    // 先在 store 列上篩(status/approvalStatus 在那裡),再投影成 DTO。
    const statusRows = filterByStatus(roleStore().all(), p.status, { defaultStatus: "啟用中" });
    let list = statusRows
      .map((r) => roleRowToCustomerRole(r, userStore().all()))
      .filter((r) => (showHidden ? true : r.active));
    if (keywords) {
      list = list.filter((r) =>
        [r.name, r.system_name, r.description].some((v) => (v || "").includes(keywords))
      );
    }
    return [200, toPageEnvelope(list, pageIndex, pageSize)];
  }));

  mock.onGet(new RegExp(`${url.DCK_BACKEND.CUSTOMER_ROLE}/GetById/\\d+$`)).reply(withAuth((config) => {
    const id = Number(config.url.split("/").pop());
    const found = roleStore().find(id);
    if (!found) {
      return [404, { Type: "about:blank", Title: "Not Found", Status: 404, Detail: "", Instance: "" }];
    }
    return [200, roleRowToCustomerRole(found, userStore().all())];
  }));

  mock.onPost(`${url.DCK_BACKEND.CUSTOMER_ROLE}/Create`).reply(withAuth((config) => {
    const dto = JSON.parse(config.data || "{}");
    if (!dto.name) return [400, ["角色名稱為必填"]];
    const row = customerRolePatchToRoleRow(dto);
    const record = roleStore().add({
      status: "啟用中",
      scope: row.scope || "自訂",
      updatedAt: new Date().toISOString().slice(0, 16).replace("T", " "),
      updatedBy: "系統",
      ...row,
      approvalStatus: "pending",
    });
    return [200, roleRowToCustomerRole(roleStore().find(record.id), userStore().all())];
  }));

  mock.onPut(`${url.DCK_BACKEND.CUSTOMER_ROLE}/Update`).reply(withAuth((config) => {
    const dto = JSON.parse(config.data || "{}");
    const found = roleStore().find(dto.id);
    if (!found) {
      return [404, { Type: "about:blank", Title: "Not Found", Status: 404, Detail: "", Instance: "" }];
    }
    const patched = roleStore().patch(dto.id, customerRolePatchToRoleRow(dto));
    return [200, roleRowToCustomerRole(patched, userStore().all())];
  }));

  mock.onDelete(new RegExp(`${url.DCK_BACKEND.CUSTOMER_ROLE}/Delete/\\d+$`)).reply(withAuth((config) => {
    const id = Number(config.url.split("/").pop());
    const found = roleStore().find(id);
    if (!found) {
      return [404, { Type: "about:blank", Title: "Not Found", Status: 404, Detail: "", Instance: "" }];
    }
    const blocked = roleStore().blockedReason(id);
    if (blocked) return [409, { message: blocked }];
    roleStore().remove(id);
    return [200];
  }));

  // Common/PhoneCountryCodes
  mock.onGet(`${url.DCK_BACKEND.COMMON}/PhoneCountryCodes`).reply(withAuth((config) => {
    // 顯示格式對齊 Figma 260804 新增使用者(2128:23439):「台灣+886」
    return [200, [
      { value: "+886", text: "台灣+886" },
      { value: "+852", text: "香港+852" },
      { value: "+86", text: "中國+86" },
      { value: "+81", text: "日本+81" },
      { value: "+1", text: "美國/加拿大+1" },
    ]];
  }));

  // —— DcRoom(DCK-03-01,ticket 08):機房/樓層/區域三層,投影自共用 room store ——
  // DTO 依 8/11 smoke 實測定版。status/is_under_review/approval_request_no/change_log
  // 為 ⚠️ 契約外(mock-only):WF 需要但後端實測沒有,已列後端待確認;hybrid 下這些
  // 欄位缺席,畫面以 active_name 降級顯示。
  const roomStore = () => getResourceStore("room");

  const roomRowToDto = (row) => ({
    id: row.id,
    name_chinese: row.name || "",
    name_english: row.nameEn || "",
    address: row.address || "",
    manager_customer_id: row.managerId != null && row.managerId !== "" ? Number(row.managerId) : 0,
    manager_customer_name: row.manager || "",
    active: row.status !== "停用",
    active_name: row.status === "停用" ? "停用" : "啟用中",
    created_on: "2026-08-01T00:00:00",
    updated_on: "2026-08-01T00:00:00",
    // ⚠️ 契約外欄位(見上)
    status: row.status || "啟用中",
    is_under_review: row.approvalStatus === "pending",
    approval_request_no: row.docNo || "",
  });
  const dcRoomPatch = (dto) => ({
    ...(dto.name_chinese !== undefined ? { name: dto.name_chinese } : {}),
    ...(dto.name_english !== undefined ? { nameEn: dto.name_english } : {}),
    ...(dto.address !== undefined ? { address: dto.address } : {}),
    ...(dto.manager_customer_id !== undefined ? { managerId: dto.manager_customer_id } : {}),
    ...(dto.manager_customer_name !== undefined ? { manager: dto.manager_customer_name } : {}),
  });
  const floorDto = (row, f, i) => ({ id: f.id, room_id: row.id, name: f.name || "", display_order: i + 1, active: true });
  const areaDto = (f, a, i) => ({ id: a.id, floor_id: f.id, name: a.name || "", display_order: i + 1, active: true });
  // 樓層/區域 id 為全域唯一(種子如此;新增時取全域 max+1)
  const allFloors = () => roomStore().all().flatMap((r) => (r.floors || []).map((f) => ({ row: r, floor: f })));
  const nextFloorId = () => Math.max(0, ...allFloors().map(({ floor }) => Number(floor.id) || 0)) + 1;
  const nextAreaId = () =>
    Math.max(0, ...allFloors().flatMap(({ floor }) => (floor.areas || []).map((a) => Number(a.id) || 0))) + 1;
  const patchFloors = (roomId, mutate) => {
    const row = roomStore().find(roomId);
    if (!row) return null;
    const floors = (row.floors || []).map((f) => ({ ...f, areas: [...(f.areas || [])] }));
    const out = mutate(floors);
    roomStore().patch(roomId, { floors });
    return out;
  };
  const roomBlockers = (roomId) => {
    const cabs = getResourceStore("cabinet").all().filter((c) => String(c.roomId) === String(roomId));
    const hws = getResourceStore("hardware").all().filter((h) => cabs.some((c) => String(c.id) === String(h.cabinetId)));
    return { cabs, hws };
  };

  mock.onGet(`${url.DCK_BACKEND.ROOM}/GetAll`).reply(withAuth((config) => {
    const p = config.params || {};
    const kw = String(p.keyword || "").trim().toLowerCase();
    let rows = roomStore().all();
    if (kw) {
      rows = rows.filter((r) =>
        [r.name, r.nameEn, r.address, r.manager].some((v) => String(v ?? "").toLowerCase().includes(kw)));
    }
    rows = filterByStatus(rows, p.status, { defaultStatus: "啟用中" });
    const pageIndex = Number(p.pageIndex) || 0;
    const pageSize = Number(p.pageSize) || 20;
    return [200, toPageEnvelope(rows.map(roomRowToDto), pageIndex, pageSize)];
  }));

  mock.onGet(new RegExp(`${url.DCK_BACKEND.ROOM}/GetById/\\d+$`)).reply(withAuth((config) => {
    const row = roomStore().find(Number(config.url.split("/").pop()));
    if (!row) return [404, "查無機房資訊"];
    // change_log 為 mock-only 契約外欄位(WF 異動歷程卡;後端無來源,已列待確認)
    return [200, { ...roomRowToDto(row), change_log: row.changeLog || [] }];
  }));

  mock.onGet(new RegExp(`${url.DCK_BACKEND.ROOM}/CanDelete/\\d+$`)).reply(withAuth((config) => {
    const id = Number(config.url.split("/").pop());
    if (!roomStore().find(id)) return [404, "查無機房資訊"];
    const { cabs, hws } = roomBlockers(id);
    return [200, {
      can_delete: cabs.length === 0 && hws.length === 0,
      rack_count: cabs.length,
      hardware_count: hws.length,
      sample_rack_nos: cabs.slice(0, 5).map((c) => c.code),
      sample_hardware_nos: hws.slice(0, 5).map((h) => h.code),
    }];
  }));

  mock.onPost(`${url.DCK_BACKEND.ROOM}/Create`).reply(withAuth((config) => {
    const dto = JSON.parse(config.data || "{}");
    const errors = [];
    if (!dto.name_chinese) errors.push("機房中文名稱為必填");
    if (!dto.name_english) errors.push("機房英文名稱為必填");
    if (!dto.address) errors.push("機房地址為必填");
    if (!dto.manager_customer_id) errors.push("管理者為必填");
    if (errors.length) return [400, errors];
    const record = roomStore().add({ ...dcRoomPatch(dto), floors: [], status: "啟用中" });
    return [200, roomRowToDto(record)];
  }));

  mock.onPut(`${url.DCK_BACKEND.ROOM}/Update`).reply(withAuth((config) => {
    const dto = JSON.parse(config.data || "{}");
    const row = roomStore().find(dto.id);
    if (!row) return [404, "查無機房資訊"];
    return [200, roomRowToDto(roomStore().patch(dto.id, dcRoomPatch(dto)))];
  }));

  // Delete:仍有機櫃/資產 → 409+阻擋原因(swagger 註記;body 形狀未定,先用 string[]
  // 讓轉譯層信任呈現,真後端實測後對齊)
  mock.onDelete(new RegExp(`${url.DCK_BACKEND.ROOM}/Delete/\\d+$`)).reply(withAuth((config) => {
    const id = Number(config.url.split("/").pop());
    const row = roomStore().find(id);
    if (!row) return [404, "查無機房資訊"];
    const { cabs, hws } = roomBlockers(id);
    if (cabs.length || hws.length) {
      return [409, [`機房「${row.name}」仍有未解除的機櫃 ${cabs.length} 座、硬體 ${hws.length} 台,無法刪除。`]];
    }
    roomStore().remove(id);
    return [200, ""];
  }));

  mock.onGet(new RegExp(`${url.DCK_BACKEND.ROOM}/GetFloors/\\d+$`)).reply(withAuth((config) => {
    const row = roomStore().find(Number(config.url.split("/").pop()));
    if (!row) return [404, "查無機房資訊"];
    return [200, (row.floors || []).map((f, i) => floorDto(row, f, i))];
  }));

  mock.onPost(`${url.DCK_BACKEND.ROOM}/CreateFloor`).reply(withAuth((config) => {
    const dto = JSON.parse(config.data || "{}");
    if (!dto.name) return [400, ["樓層名稱為必填"]];
    const row = roomStore().find(dto.room_id);
    if (!row) return [404, "查無機房資訊"];
    const floor = { id: nextFloorId(), name: dto.name, areas: [] };
    patchFloors(dto.room_id, (floors) => floors.push(floor));
    return [200, floorDto(row, floor, (row.floors || []).length)];
  }));

  mock.onPut(`${url.DCK_BACKEND.ROOM}/UpdateFloor`).reply(withAuth((config) => {
    const dto = JSON.parse(config.data || "{}");
    const hit = allFloors().find(({ floor }) => floor.id === dto.id);
    if (!hit) return [404, "查無樓層"];
    patchFloors(hit.row.id, (floors) => {
      const f = floors.find((x) => x.id === dto.id);
      if (f && dto.name !== undefined) f.name = dto.name;
    });
    return [200, floorDto(hit.row, { ...hit.floor, name: dto.name ?? hit.floor.name }, 0)];
  }));

  mock.onDelete(new RegExp(`${url.DCK_BACKEND.ROOM}/DeleteFloor/\\d+$`)).reply(withAuth((config) => {
    const id = Number(config.url.split("/").pop());
    const hit = allFloors().find(({ floor }) => floor.id === id);
    if (!hit) return [404, "查無樓層"];
    patchFloors(hit.row.id, (floors) => floors.splice(floors.findIndex((x) => x.id === id), 1));
    return [200, ""];
  }));

  mock.onGet(new RegExp(`${url.DCK_BACKEND.ROOM}/GetAreas/\\d+$`)).reply(withAuth((config) => {
    const id = Number(config.url.split("/").pop());
    const hit = allFloors().find(({ floor }) => floor.id === id);
    if (!hit) return [404, "查無樓層"];
    return [200, (hit.floor.areas || []).map((a, i) => areaDto(hit.floor, a, i))];
  }));

  mock.onGet(new RegExp(`${url.DCK_BACKEND.ROOM}/GetAreasByRoom/\\d+$`)).reply(withAuth((config) => {
    const row = roomStore().find(Number(config.url.split("/").pop()));
    if (!row) return [404, "查無機房資訊"];
    return [200, (row.floors || []).flatMap((f) => (f.areas || []).map((a, i) => areaDto(f, a, i)))];
  }));

  mock.onPost(`${url.DCK_BACKEND.ROOM}/CreateArea`).reply(withAuth((config) => {
    const dto = JSON.parse(config.data || "{}");
    if (!dto.name) return [400, ["區域名稱為必填"]];
    const hit = allFloors().find(({ floor }) => floor.id === dto.floor_id);
    if (!hit) return [404, "查無樓層"];
    const area = { id: nextAreaId(), name: dto.name };
    patchFloors(hit.row.id, (floors) => {
      floors.find((x) => x.id === dto.floor_id).areas.push(area);
    });
    return [200, areaDto(hit.floor, area, (hit.floor.areas || []).length)];
  }));

  mock.onPut(`${url.DCK_BACKEND.ROOM}/UpdateArea`).reply(withAuth((config) => {
    const dto = JSON.parse(config.data || "{}");
    const hit = allFloors().find(({ floor }) => (floor.areas || []).some((a) => a.id === dto.id));
    if (!hit) return [404, "查無區域"];
    patchFloors(hit.row.id, (floors) => {
      floors.forEach((f) => {
        const a = (f.areas || []).find((x) => x.id === dto.id);
        if (a && dto.name !== undefined) a.name = dto.name;
      });
    });
    return [200, areaDto(hit.floor, { id: dto.id, name: dto.name }, 0)];
  }));

  mock.onDelete(new RegExp(`${url.DCK_BACKEND.ROOM}/DeleteArea/\\d+$`)).reply(withAuth((config) => {
    const id = Number(config.url.split("/").pop());
    const hit = allFloors().find(({ floor }) => (floor.areas || []).some((a) => a.id === id));
    if (!hit) return [404, "查無區域"];
    patchFloors(hit.row.id, (floors) => {
      floors.forEach((f) => {
        const idx = (f.areas || []).findIndex((x) => x.id === id);
        if (idx >= 0) f.areas.splice(idx, 1);
      });
    });
    return [200, ""];
  }));

  // —— DcRack(DCK-05-01,票 20):投影自共用 cabinet store ——
  // 契約依 swagger v1.00(**文件層**,8/12 runtime 仍 500;第一次跑得動時以 smoke 實測為準)。
  // 關鍵取捨(裁示 8/14「U 用量以後端為準」):`used_u`／`used_pdu_count` 在真後端是
  // 後端算好的欄位,所以 mock 也**在投影時算完**再吐出來 —— 讓 mock 與 hybrid 的
  // 資料形狀一致,頁面只有一種讀法(讀 DTO 的欄位),不會出現「mock 走推導、
  // 真後端走欄位」兩套算法。
  const cabinetStoreOf = () => getResourceStore("cabinet");

  // ⚠️ `manager` 是 decorate 出來的欄位(由工作項目回填,見 assetManager),
  // 不在 store 的原始列上 —— 直接讀 row.manager 會是空字串,
  // 於是「管理者」欄整排空白、關鍵字也搜不到人名。投影前先套同一份 decorate。
  const decorateCabinet = decorateManager("cabinet");

  const rackRowToDto = (raw) => {
    const row = decorateCabinet(raw);
    const hardware = getResourceStore("hardware").all();
    const { occupied, reserved } = uUsageOf(row.id, hardware);
    return {
      id: row.id,
      room_id: Number(row.roomId) || 0,
      floor_id: row.floorId != null && row.floorId !== "" ? Number(row.floorId) : null,
      area_id: row.areaId != null && row.areaId !== "" ? Number(row.areaId) : null,
      business_category_id: 0,
      business_category_name: row.bizType || "",
      is_key_business: row.keyBiz === "啟用",
      manager_customer_id: Number(row.managerId) || 0,
      manager_customer_name: row.manager || "",
      asset_name: row.name || "",
      asset_category_id: 0,
      asset_category_name: row.assetType || "",
      asset_no: row.assetCode || "",
      brand: row.brand || "",
      model: row.model || "",
      // 2026-09-08 mock-vs-real:實測 DcRackDto 有 brand_id／model_id(number),補上
      brand_id: Number(row.brandId) || 0,
      model_id: Number(row.modelId) || 0,
      rack_no: row.code || "",
      power_circuit: row.powerCircuit || "",
      voltage: row.voltage || "",
      capacity_kva: Number(row.capacityKva) || 0,
      pdu_count: Number(row.pduCount) || 0,
      used_pdu_count: Number(row.pduUsed) || 0,
      left_power_count: Number(row.leftPower) || 0,
      right_power_count: Number(row.rightPower) || 0,
      total_u: Number(row.uTotal) || 0,
      // 由上架硬體推導(佔用＋保留),與 uSlots 同一份算法 —— 真後端則是自己算好給
      used_u: occupied + reserved,
      mounted_date: row.mountDate || null,
      purchase_amount: Number(row.purchaseAmount) || 0,
      currency_code: row.currency || "TWD",
      description: row.description || "",
      active: row.status !== "停用",
      active_name: row.status === "停用" ? "停用" : "啟用中",
      created_on: "2026-08-01T00:00:00",
      updated_on: "2026-08-01T00:00:00",
      // ⚠️ 契約外(mock-only):WF 的審核註記,後端 DcRackDto 沒有這兩欄。
      // hybrid 下缺席 → 畫面以 active_name 降級(與 DcRoom 同一種處理)
      status: row.status || "啟用中",
      is_under_review: row.approvalStatus === "pending",
      approval_request_no: row.docNo || "",
      // ⚠️ 契約外(mock-only,WF_v3 票 21):結束攤提日＋維修中的替代機資訊
      amort_end_date: row.amortEndDate || null,
      spare_brand: row.spareBrand || "",
      spare_model: row.spareModel || "",
    };
  };

  /** DTO → cabinet store 的欄位(只收後端會送的那些) */
  const dcRackPatch = (dto) => ({
    ...(dto.amort_end_date !== undefined ? { amortEndDate: dto.amort_end_date } : {}),
    ...(dto.rack_no !== undefined ? { code: dto.rack_no } : {}),
    ...(dto.room_id !== undefined ? { roomId: dto.room_id } : {}),
    ...(dto.floor_id !== undefined ? { floorId: dto.floor_id } : {}),
    ...(dto.area_id !== undefined ? { areaId: dto.area_id } : {}),
    ...(dto.business_category_name !== undefined ? { bizType: dto.business_category_name } : {}),
    ...(dto.is_key_business !== undefined ? { keyBiz: dto.is_key_business ? "啟用" : "不啟用" } : {}),
    ...(dto.manager_customer_id !== undefined ? { managerId: dto.manager_customer_id } : {}),
    ...(dto.manager_customer_name !== undefined ? { manager: dto.manager_customer_name } : {}),
    ...(dto.asset_name !== undefined ? { name: dto.asset_name } : {}),
    ...(dto.asset_no !== undefined ? { assetCode: dto.asset_no } : {}),
    ...(dto.brand !== undefined ? { brand: dto.brand } : {}),
    ...(dto.model !== undefined ? { model: dto.model } : {}),
    ...(dto.power_circuit !== undefined ? { powerCircuit: dto.power_circuit } : {}),
    ...(dto.voltage !== undefined ? { voltage: dto.voltage } : {}),
    ...(dto.capacity_kva !== undefined ? { capacityKva: dto.capacity_kva } : {}),
    ...(dto.left_power_count !== undefined ? { leftPower: dto.left_power_count } : {}),
    ...(dto.right_power_count !== undefined ? { rightPower: dto.right_power_count } : {}),
    ...(dto.total_u !== undefined ? { uTotal: dto.total_u } : {}),
    ...(dto.mounted_date !== undefined ? { mountDate: dto.mounted_date } : {}),
    ...(dto.purchase_amount !== undefined ? { purchaseAmount: dto.purchase_amount } : {}),
    ...(dto.description !== undefined ? { description: dto.description } : {}),
  });

  const rackHardware = (rackId) =>
    getResourceStore("hardware").all().filter((h) => String(h.cabinetId) === String(rackId));

  mock.onGet(`${url.DCK_BACKEND.RACK}/GetAll`).reply(withAuth((config) => {
    const p = config.params || {};
    const kw = String(p.keyword || "").trim().toLowerCase();
    // 先 decorate 再篩:`manager` 是回填出來的欄位,不先套就搜不到人名
    // (crudFactory 的 REST 版也是這個順序 —— 兩邊不一致的話,同一個關鍵字在
    //  mock 與 hybrid 會得到不同結果)
    let rows = cabinetStoreOf().all().map(decorateCabinet);
    rows = filterByStatus(rows, p.status);
    // query 語意照 swagger:0 = 不限制
    if (Number(p.roomId)) rows = rows.filter((r) => String(r.roomId) === String(p.roomId));
    if (Number(p.managerCustomerId)) rows = rows.filter((r) => String(r.managerId) === String(p.managerCustomerId));
    if (p.active !== undefined && p.active !== null && p.active !== "") {
      const want = p.active === true || p.active === "true";
      rows = rows.filter((r) => (r.status !== "停用") === want);
    }
    if (kw) {
      rows = rows.filter((r) =>
        [r.code, r.brand, r.model, r.manager].some((v) => String(v ?? "").toLowerCase().includes(kw)));
    }
    const pageIndex = Number(p.pageIndex) || 0;
    const pageSize = Number(p.pageSize) || 20;
    return [200, toPageEnvelope(rows.map(rackRowToDto), pageIndex, pageSize)];
  }));

  mock.onGet(new RegExp(`${url.DCK_BACKEND.RACK}/GetById/\\d+$`)).reply(withAuth((config) => {
    const row = cabinetStoreOf().find(Number(config.url.split("/").pop()));
    if (!row) return [404, "查無機櫃資訊"];
    return [200, { ...rackRowToDto(row), change_log: row.changeLog || [] }];
  }));

  // RackDeletionCheckResultDto:只有硬體,沒有機櫃樣本欄(與 DcRoom 不同型別)
  mock.onGet(new RegExp(`${url.DCK_BACKEND.RACK}/CanDelete/\\d+$`)).reply(withAuth((config) => {
    const id = Number(config.url.split("/").pop());
    if (!cabinetStoreOf().find(id)) return [404, "查無機櫃資訊"];
    const hws = rackHardware(id);
    return [200, {
      can_delete: hws.length === 0,
      hardware_count: hws.length,
      sample_hardware_nos: hws.slice(0, 3).map((h) => h.code),
    }];
  }));

  mock.onPost(`${url.DCK_BACKEND.RACK}/Create`).reply(withAuth((config) => {
    const dto = JSON.parse(config.data || "{}");
    // swagger 的 required 是空的,但描述寫了必填 —— mock 照描述擋,免得前端漏驗到正式環境才炸
    const errors = [];
    if (!dto.rack_no) errors.push("機櫃編號為必填");
    if (!dto.room_id) errors.push("機房為必填");
    if (!dto.brand) errors.push("廠牌為必填");
    if (!dto.model) errors.push("型號為必填");
    if (errors.length) return [400, errors];
    const record = cabinetStoreOf().add({ ...dcRackPatch(dto), status: "啟用中" });
    return [200, rackRowToDto(record)];
  }));

  mock.onPut(`${url.DCK_BACKEND.RACK}/Update`).reply(withAuth((config) => {
    const dto = JSON.parse(config.data || "{}");
    if (!cabinetStoreOf().find(dto.id)) return [404, "查無機櫃資訊"];
    cabinetStoreOf().patch(dto.id, dcRackPatch(dto));
    return [200, rackRowToDto(cabinetStoreOf().find(dto.id))];
  }));

  mock.onDelete(new RegExp(`${url.DCK_BACKEND.RACK}/Delete/\\d+$`)).reply(withAuth((config) => {
    const id = Number(config.url.split("/").pop());
    const row = cabinetStoreOf().find(id);
    if (!row) return [404, "查無機櫃資訊"];
    const hws = rackHardware(id);
    // 409 的 body 是 RackDeletionCheckResultDto(與 CanDelete 同一份),不是字串陣列
    if (hws.length) {
      return [409, {
        can_delete: false,
        hardware_count: hws.length,
        sample_hardware_nos: hws.slice(0, 3).map((h) => h.code),
      }];
    }
    cabinetStoreOf().remove(id);
    return [200, ""];
  }));

  // —— DcIpAddress(DCK-04-01,票 9月交付/16):投影自 "ip" store,關聯資產查 hardware／cabinet store ——
  // 契約:9/14 swagger(SIT＝UAT)＋ 9/13 SIT smoke 實測(GetAll 20 鍵／子端點 34＋9 鍵)。
  // 後端把「關聯」拆成子端點、把「位置／主要資產／專案／廠商」算成列表唯讀欄位 ——
  // mock 照做:store 只存參照,投影時查表算出來,讓 mock 與真後端的形狀一致(ADR-0007)。
  const ipStoreOf = () => getResourceStore("ip");
  const userById = (id) => (getResourceStore("user")?.find(id)) || null;
  const roomById = (id) => (getResourceStore("room")?.find(id)) || null;
  const IP_ASSET_TYPE_NAME = { Hardware: "硬體", Rack: "機櫃" };
  const CONTRACT_ASSET_TYPE_NAME = { Hardware: "硬體", Rack: "機櫃", Software: "軟體" };
  const decorateIpHardware = decorateManager("hardware");

  /**
   * 關聯參照 → 資產主檔(找不到回 null:資產被刪了,投影時略過並 warn 一次)。
   * IP 只掛硬體／機櫃;合約還可掛軟體(ContractAssetType)—— 同一支查表,呼叫端自己限制類型。
   */
  const linkedAssetOf = (ref) => {
    if (ref.assetType === "Hardware") { const h = getResourceStore("hardware").find(ref.assetId); return h ? decorateIpHardware(h) : null; }
    if (ref.assetType === "Rack") { const c = cabinetStoreOf().find(ref.assetId); return c ? decorateCabinet(c) : null; }
    if (ref.assetType === "Software") { const s = getResourceStore("software")?.find(ref.assetId); return s || null; }
    return null;
  };
  const linkedAssetLocation = (ref, asset) => {
    // 軟體沒有位置(部署在硬體上,位置歸硬體)
    const cab = ref.assetType === "Rack" ? asset : (ref.assetType === "Hardware" ? cabinetStoreOf().find(asset.cabinetId) : null);
    const room = cab ? roomById(cab.roomId) : null;
    return {
      cabinet: cab || null, room: room || null,
      floorId: cab ? cab.floorId : null, areaId: cab ? cab.areaId : null,
    };
  };
  /** user store 的列 → DTO 的 manager_*／ip_manager_* 八鍵(prefix 決定是哪一組) */
  const managerKeys = (prefix, user, fallbackId) => ({
    [`${prefix}customer_id`]: user ? user.id : (Number(fallbackId) || 0),
    [`${prefix}customer_name`]: user ? user.name : null,
    [`${prefix}employee_no`]: user ? user.employeeId || null : null,
    [`${prefix}unit_name`]: user ? user.department || null : null,
    [`${prefix}job_title`]: user ? user.title || null : null,
    [`${prefix}role_name`]: user ? user.roleName || null : null,
    [`${prefix}email`]: user ? user.email || null : null,
    [`${prefix}extension`]: user ? user.officeExt || null : null,
  });

  /**
   * 資產關聯 DTO 的共同 22 鍵(IP 的 34 鍵與合約的 24 鍵前段一模一樣):資產本體＋位置＋管理者八鍵＋OS／EOL／EOS。
   * `typeNames` 決定 asset_type_name 的對照表(IP 兩種、合約三種)。
   */
  const linkedAssetKeys = (ref, asset, typeNames) => {
    const isHw = ref.assetType === "Hardware";
    const { cabinet, room } = linkedAssetLocation(ref, asset);
    const managerUser = asset.managerId != null ? userById(asset.managerId) : null;
    return {
      asset_type: ref.assetType,
      asset_type_name: typeNames[ref.assetType] || null,
      asset_id: asset.id,
      asset_no: asset.code || null,
      asset_name: asset.name || null,
      brand: asset.brand || null,
      model: asset.model || null,
      room_name: room ? room.name : null,
      rack_no: cabinet ? cabinet.code : null,
      position_u: isHw ? formatUPosition(asset.uPosition, asset.uSize) || null : null,
      ip_address: isHw ? asset.ip || null : null,
      ...managerKeys("manager_", managerUser, asset.managerId),
      operating_system: isHw ? asset.os || null : null,
      eol_date: isHw && asset.eol ? `${asset.eol}T00:00:00` : null,
      eos_date: isHw && asset.eos ? `${asset.eos}T00:00:00` : null,
    };
  };

  /** DcIpAddressAssetMappingDto(34 鍵)。ipRow 給了才附 IP 本身資訊(swagger:需指定 ipAddressId) */
  const ipMappingDto = (ref, asset, ipRow) => {
    const ipManagerUser = ipRow ? userById(ipRow.managerId) : null;
    return {
      id: ref.id || 0,
      ip_address_id: ipRow ? ipRow.id : 0,
      ...linkedAssetKeys(ref, asset, IP_ASSET_TYPE_NAME),
      // swagger:帶 ipAddressId 時改顯示該 IP 本身的位置(資產詳情彈窗的「IP 資訊」欄)
      ...(ipRow ? { ip_address: ipRow.ip } : {}),
      parent_ip_address: ipRow ? ipRow.parentIp || null : null,
      subnet_mask: ipRow ? ipRow.mask || null : null,
      ...managerKeys("ip_manager_", ipManagerUser, ipRow ? ipRow.managerId : 0),
    };
  };
  const ipMappingsOf = (row, { warnMissing = true } = {}) =>
    (row.assets || []).map((ref) => {
      const asset = linkedAssetOf(ref);
      if (!asset && warnMissing) {
        console.warn(`[mock] IP ${row.ip} 的關聯 ${ref.assetType}#${ref.assetId} 在資產 store 找不到,投影略過`);
      }
      return asset ? ipMappingDto(ref, asset, row) : null;
    }).filter(Boolean);

  /** 列表列 DcIpAddressListItemDto(20 鍵):位置／主要資產取第一筆關聯;專案／廠商 mock-only 推算 */
  const ipStatusKeys = (row) => {
    const underReview = row.approvalStatus === "pending";
    return {
      data_source: row.source || "人工建檔",
      pending_review: !!row.pendingReview,
      is_under_review: underReview,
      approval_request_no: underReview ? row.docNo || null : null,
      // 後端算好的顯示文字;值域照 swagger(異動中／審核中｜RQ-…)
      status_text: underReview ? `審核中｜${row.docNo || ""}` : (row.pendingReview ? "異動中" : null),
    };
  };
  const ipRowToListItem = (row) => {
    const first = ipMappingsOf(row, { warnMissing: false })[0] || null;
    const manager = userById(row.managerId);
    return {
      id: row.id,
      ip_address: row.ip || null,
      parent_ip_address: row.parentIp || null,
      subnet_mask: row.mask || null,
      default_gateway: row.gateway || null,
      primary_asset_name: first ? first.asset_name : null,
      primary_asset_no: first ? first.asset_no : null,
      room_name: first ? first.room_name : null,
      rack_no: first ? first.rack_no : null,
      position_u: first ? first.position_u : null,
      // ⚠️ mock-only 推算來源(後端由關聯資產的專案／合約算;DcProject／DcVendor 尚未到位)
      support_project_name: row.projects && row.projects[0] ? row.projects[0].name : null,
      support_project_id: null, // DcProject 未到位,mock 專案沒有後端 id

      maintenance_vendor_name: row.vendors && row.vendors[0] ? row.vendors[0] : null,
      manager_customer_id: Number(row.managerId) || 0,
      manager_customer_name: manager ? manager.name : (row.manager || null),
      ...ipStatusKeys(row),
    };
  };
  /** 實體 DcIpAddressDto(GetById／Create／Update 回應) */
  const ipRowToDto = (row) => {
    const manager = userById(row.managerId);
    return {
      id: row.id,
      ip_address: row.ip || null,
      parent_ip_address: row.parentIp || null,
      ip_port: row.port || null,
      subnet_mask: row.mask || null,
      default_gateway: row.gateway || null,
      dns_server: row.dns || null,
      mac_address: row.mac || null,
      physical_port: row.iface || null,
      manager_customer_id: Number(row.managerId) || 0,
      manager_customer_name: manager ? manager.name : (row.manager || null),
      last_probed_on: row.lastProbedOn || null,
      // 實體 DTO 沒有 status_text;其餘狀態鍵與列表列同一份算法
      ...(({ status_text, ...keys }) => keys)(ipStatusKeys(row)),
      asset_mappings: ipMappingsOf(row),
      created_on: row.createdOn || "2026-06-12T10:02:00",
      updated_on: row.updatedOn || "2026-08-05T14:22:00",
    };
  };
  /** DTO → store 欄位(只收後端會存的那些;唯讀／狀態欄不收) */
  const dcIpPatch = (dto) => ({
    ...(dto.ip_address !== undefined ? { ip: dto.ip_address } : {}),
    ...(dto.parent_ip_address !== undefined ? { parentIp: dto.parent_ip_address } : {}),
    ...(dto.subnet_mask !== undefined ? { mask: dto.subnet_mask } : {}),
    ...(dto.default_gateway !== undefined ? { gateway: dto.default_gateway } : {}),
    ...(dto.dns_server !== undefined ? { dns: dto.dns_server } : {}),
    ...(dto.mac_address !== undefined ? { mac: dto.mac_address } : {}),
    ...(dto.physical_port !== undefined ? { iface: dto.physical_port } : {}),
    ...(dto.manager_customer_id !== undefined ? { managerId: Number(dto.manager_customer_id) || 0 } : {}),
  });
  /** 異動歷程 store 列({at,by,kind,text,docNo,result},審核引擎回寫的形狀)→ DcIpAddressChangeLogDto(9 鍵) */
  const ipHistoryToChangeLog = (h, i) => {
    const m = /^(.*?)（([^）]+)）$/.exec(h.by || "");
    return {
      id: i + 1,
      changed_on: h.at ? `${h.at.slice(0, 10)}T${h.at.slice(11, 16) || "00:00"}:00` : null,
      changed_by_name: m ? m[1] : (h.by || null),
      changed_by_employee_no: m ? m[2] : null,
      action: h.kind || null,
      description: h.text || null,
      request_no: h.docNo || null,
      status_text: h.result || null,
      is_system_generated: !h.docNo,
    };
  };
  const ipIdOf = (config) => Number(config.url.split("/").pop());

  mock.onGet(`${url.DCK_BACKEND.IP_ADDRESS}/GetAll`).reply(withAuth((config) => {
    const p = config.params || {};
    const kw = String(p.keyword || "").trim().toLowerCase();
    let rows = ipStoreOf().all();
    // query 語意照 swagger:0 = 不限制;機房／樓層／區域看第一筆關聯資產的位置
    const locOf = (row) => {
      const ref = (row.assets || [])[0];
      const asset = ref ? linkedAssetOf(ref) : null;
      return asset ? linkedAssetLocation(ref, asset) : { room: null, floorId: null, areaId: null };
    };
    if (Number(p.roomId)) rows = rows.filter((r) => String(locOf(r).room?.id) === String(p.roomId));
    if (Number(p.floorId)) rows = rows.filter((r) => String(locOf(r).floorId) === String(p.floorId));
    if (Number(p.areaId)) rows = rows.filter((r) => String(locOf(r).areaId) === String(p.areaId));
    if (Number(p.managerCustomerId)) rows = rows.filter((r) => String(r.managerId) === String(p.managerCustomerId));
    if (kw) {
      // swagger:IP／Interface(介面位置、IP Port 位)／Asset(關聯資產名稱、編號)／MAC
      rows = rows.filter((r) => {
        const assets = ipMappingsOf(r, { warnMissing: false });
        return [r.ip, r.iface, r.port, r.mac, ...assets.flatMap((a) => [a.asset_name, a.asset_no])]
          .some((v) => String(v ?? "").toLowerCase().includes(kw));
      });
    }
    const pageIndex = Number(p.pageIndex) || 0;
    const pageSize = Number(p.pageSize) || 20;
    return [200, toPageEnvelope(rows.map(ipRowToListItem), pageIndex, pageSize)];
  }));

  mock.onGet(new RegExp(`${url.DCK_BACKEND.IP_ADDRESS}/GetById/\\d+$`)).reply(withAuth((config) => {
    const row = ipStoreOf().find(ipIdOf(config));
    if (!row) return [404, "查無 IP 資訊"];
    return [200, ipRowToDto(row)];
  }));

  // 必填只寫在 description(swagger required 是空的)—— mock 照描述擋,400 回 string[](與其他 controller 同)
  const ipRequiredErrors = (dto) => {
    const errors = [];
    if (!String(dto.ip_address || "").trim()) errors.push("IP 資訊為必填");
    if (!String(dto.parent_ip_address || "").trim()) errors.push("上一層 IP 為必填");
    if (!String(dto.subnet_mask || "").trim()) errors.push("子網路遮罩為必填");
    if (!String(dto.default_gateway || "").trim()) errors.push("預設閘道為必填");
    if (!Number(dto.manager_customer_id)) errors.push("IP 管理者為必填");
    return errors;
  };

  mock.onPost(`${url.DCK_BACKEND.IP_ADDRESS}/Create`).reply(withAuth((config) => {
    const dto = JSON.parse(config.data || "{}");
    const errors = ipRequiredErrors(dto);
    if (errors.length) return [400, errors];
    const record = ipStoreOf().add({
      ...dcIpPatch(dto),
      port: "", source: "人工建檔", lastProbedOn: null, pendingReview: false, approvalStatus: "approved",
      assets: [], projects: [], vendors: [], history: [],
      createdOn: new Date().toISOString().slice(0, 19), updatedOn: new Date().toISOString().slice(0, 19),
    });
    return [200, ipRowToDto(record)];
  }));

  mock.onPut(`${url.DCK_BACKEND.IP_ADDRESS}/Update`).reply(withAuth((config) => {
    const dto = JSON.parse(config.data || "{}");
    if (!ipStoreOf().find(dto.id)) return [404, "查無 IP 資訊"];
    const errors = ipRequiredErrors(dto);
    if (errors.length) return [400, errors];
    ipStoreOf().patch(dto.id, { ...dcIpPatch(dto), updatedOn: new Date().toISOString().slice(0, 19) });
    return [200, ipRowToDto(ipStoreOf().find(dto.id))];
  }));

  // ⚠️ swagger 的 Delete 只宣告 200／400(ProblemDetails)／404,**沒有 409 也沒有 CanDelete**。
  // 「還有關聯時刪除會怎樣」後端未答(票 28):mock 先以 400 ProblemDetails 擋(不是 string[] 也不是
  // {message}),讓 errorMessages 走制式 400 文案 —— 前端因此**必須自己先用 GetAssetMappings 預檢**
  // 才講得出「仍關聯 N 筆」。後端答了再改這裡與頁面。
  mock.onDelete(new RegExp(`${url.DCK_BACKEND.IP_ADDRESS}/Delete/\\d+$`)).reply(withAuth((config) => {
    const id = ipIdOf(config);
    const row = ipStoreOf().find(id);
    if (!row) return [404, "查無 IP 資訊"];
    if ((row.assets || []).length) {
      return [400, { type: "about:blank", title: "IP 仍有資產關聯", status: 400, detail: `IP ${row.ip} 仍關聯 ${row.assets.length} 筆資產` }];
    }
    ipStoreOf().remove(id);
    return [200, ""];
  }));

  mock.onGet(`${url.DCK_BACKEND.IP_ADDRESS}/GetAssetMappings`).reply(withAuth((config) => {
    const id = Number((config.params || {}).ipAddressId);
    if (!id) return [400, ["ipAddressId 為必填"]];
    const row = ipStoreOf().find(id);
    if (!row) return [404, "查無 IP 資訊"];
    return [200, ipMappingsOf(row)];
  }));

  let nextIpMappingId = ips.reduce((max, r) => Math.max(max, ...(r.assets || []).map((a) => a.id || 0)), 0) + 1;
  // POST 三個 query 參數、無 body(swagger);assetType 是 DcAssetType,只有 Hardware／Rack 可掛
  mock.onPost(new RegExp(`${url.DCK_BACKEND.IP_ADDRESS}/AddAssetMapping`)).reply(withAuth((config) => {
    const q = new URLSearchParams(config.url.split("?")[1] || "");
    const p = { ...Object.fromEntries(q.entries()), ...(config.params || {}) };
    const ipAddressId = Number(p.ipAddressId);
    const assetId = Number(p.assetId);
    const assetType = String(p.assetType || "");
    const row = ipStoreOf().find(ipAddressId);
    if (!row) return [404, "查無 IP 資訊"];
    if (!IP_ASSET_TYPE_NAME[assetType]) return [400, [`assetType「${assetType}」不可關聯(僅 Hardware／Rack)`]];
    if (!linkedAssetOf({ assetType, assetId })) return [404, "查無資產"];
    if ((row.assets || []).some((a) => a.assetType === assetType && String(a.assetId) === String(assetId))) {
      return [400, ["此資產已關聯至該 IP"]];
    }
    ipStoreOf().patch(ipAddressId, { assets: [...(row.assets || []), { id: nextIpMappingId++, assetType, assetId }] });
    return [200, ""];
  }));

  mock.onDelete(new RegExp(`${url.DCK_BACKEND.IP_ADDRESS}/RemoveAssetMapping/\\d+$`)).reply(withAuth((config) => {
    const mappingId = ipIdOf(config);
    const row = ipStoreOf().all().find((r) => (r.assets || []).some((a) => Number(a.id) === mappingId));
    if (!row) return [404, "查無資產關聯"];
    ipStoreOf().patch(row.id, { assets: row.assets.filter((a) => Number(a.id) !== mappingId) });
    return [200, ""];
  }));

  // 可掛的資產＝硬體＋機櫃(swagger:assetType null 或 All 表示兩者都搜);全部條件選填。
  // 帶 ipAddressId 時每筆附 IP 本身資訊(資產詳情彈窗用)。swagger **沒說**會排除該 IP 已加入的,
  // mock 就不排除(抄契約不猜;「已加入的不再列出」由前端拿已加入清單過濾)
  mock.onGet(`${url.DCK_BACKEND.IP_ADDRESS}/SearchLinkableAssets`).reply(withAuth((config) => {
    const p = config.params || {};
    const hit = (val, needle) => !needle || String(val || "").toLowerCase().includes(String(needle).toLowerCase());
    const ipRow = Number(p.ipAddressId) ? ipStoreOf().find(Number(p.ipAddressId)) : null;
    const type = p.assetType && p.assetType !== "All" ? String(p.assetType) : null;
    const refs = [
      ...(type === "Rack" ? [] : getResourceStore("hardware").all().map((h) => ({ assetType: "Hardware", assetId: h.id }))),
      ...(type === "Hardware" ? [] : cabinetStoreOf().all().map((c) => ({ assetType: "Rack", assetId: c.id }))),
    ];
    const out = refs
      .map((ref) => ({ ref, asset: linkedAssetOf(ref) }))
      .filter(({ asset }) => asset)
      .filter(({ asset }) => hit(`${asset.name} ${asset.code}`, p.assetName))
      .filter(({ ref, asset }) => !Number(p.roomId) || String(linkedAssetLocation(ref, asset).room?.id) === String(p.roomId))
      .filter(({ asset }) => hit(asset.brand, p.brand))
      .filter(({ asset }) => hit(asset.model, p.model))
      .filter(({ asset }) => !Number(p.managerCustomerId) || String(asset.managerId) === String(p.managerCustomerId))
      .filter(({ ref, asset }) => !p.ip || (ref.assetType === "Hardware" && hit(asset.ip, p.ip)))
      // 搜尋結果:id／ip_address_id 為 0(尚未加入);帶 ipAddressId 才附 IP 本身資訊
      .map(({ ref, asset }) => ({ ...ipMappingDto({ ...ref, id: 0 }, asset, ipRow), id: 0, ip_address_id: 0 }));
    return [200, out];
  }));

  mock.onGet(`${url.DCK_BACKEND.IP_ADDRESS}/GetChangeHistory`).reply(withAuth((config) => {
    const id = Number((config.params || {}).ipAddressId);
    if (!id) return [400, ["ipAddressId 為必填"]];
    const row = ipStoreOf().find(id);
    if (!row) return [404, "查無 IP 資訊"];
    // 新到舊(store 的 history 本來就是新的在前)
    return [200, (row.history || []).map(ipHistoryToChangeLog)];
  }));

  // 確認已檢視 SNMP 自動更新內容,清除「異動中」。PUT 無 body
  mock.onPut(new RegExp(`${url.DCK_BACKEND.IP_ADDRESS}/ConfirmReview/\\d+$`)).reply(withAuth((config) => {
    const id = ipIdOf(config);
    const row = ipStoreOf().find(id);
    if (!row) return [404, "查無 IP 資訊"];
    if (!row.pendingReview) return [400, { type: "about:blank", title: "此 IP 沒有待確認的異動", status: 400 }];
    ipStoreOf().patch(id, { pendingReview: false });
    return [200, ""];
  }));

  // —— DcContract 讀取側(DCK-04-03,票 9月交付/33):投影自 "contract" store ——
  // 契約:9/14 swagger ＋ 9/13 SIT smoke(GetAll 19 鍵、GetAssetMappings／SearchLinkableAssets 24 鍵實測;
  // GetRelations／GetChangeHistory／GetMaintenanceRecords 在 SIT 是**空陣列**,形狀照 swagger 寫,⚠️ 沒抄到實測)。
  // 寫入端點(Create／Update／Delete／關聯增刪／維護紀錄)歸票 18,這裡不做;頁面寫入在票 18 前仍走 /api/contracts。
  const contractStoreOf = () => getResourceStore("contract");
  const vendorIdOf = (name) => (getResourceStore("vendor")?.all().find((v) => v.name === name) || {}).id || 0;
  const lookupId = (list, text) => (list.find((o) => o.text === text) || {}).value || 0;
  const dateOnlyOf = (d) => (typeof d === "string" && d.length >= 10 ? d.slice(0, 10) : "");
  const isoDate = (d) => (d ? `${dateOnlyOf(d)}T00:00:00` : null);
  // 後端算好的狀態文字(值域照 swagger:正常／剩 N 天／已逾期 N 天／審核中｜RQ-…)。
  // ⚠️ 「剩 N 天」從幾天開始算後端沒說(待確認清單一、6);mock 先沿用前端原本的 90 天
  const CONTRACT_EXPIRY_WARN_DAYS = 90;
  const contractStatusText = (row) => {
    if (row.approvalStatus === "pending") return `審核中｜${row.docNo || ""}`;
    if (!row.endDate) return "正常";
    const diff = Math.ceil((new Date(row.endDate).getTime() - Date.now()) / 86400000);
    if (diff < 0) return `已逾期 ${Math.abs(diff)} 天`;
    if (diff <= CONTRACT_EXPIRY_WARN_DAYS) return `剩 ${diff} 天`;
    return "正常";
  };
  // 金額遮蔽由後端做(無欄位權限者 amount=0、amount_text=*****);mock 沒有欄位權限的概念,一律給全值
  const amountText = (amount) => `NT$ ${Number(amount || 0).toLocaleString("en-US")}`;
  const contractCommon = (row) => {
    const manager = userById(row.managerId);
    return {
      contract_no: row.code || null,
      name: row.name || null,
      manager_customer_id: Number(row.managerId) || 0,
      manager_customer_name: manager ? manager.name : (row.manager || null),
      start_date: isoDate(row.startDate),
      end_date: isoDate(row.endDate),
      year_limit: Number(row.years) || 0,
      service_level_id: lookupId(SERVICE_LEVELS, row.sla),
      service_level_name: row.sla || null,
      maintenance_cycle_id: lookupId(MAINTENANCE_CYCLES, row.cycle),
      maintenance_cycle_name: row.cycle || null,
      vendor_id: vendorIdOf(row.vendorName),
      vendor_name: row.vendorName || null,
      amount: Number(row.amount) || 0,
      amount_text: amountText(row.amount),
      is_under_review: row.approvalStatus === "pending",
      approval_request_no: row.approvalStatus === "pending" ? row.docNo || null : null,
    };
  };
  /** DcContractListItemDto(19 鍵,對 9/13 smoke) */
  const contractRowToListItem = (row) => ({
    id: row.id,
    ...contractCommon(row),
    status_text: contractStatusText(row),
  });
  /** DcContractDto(33 鍵,照 swagger) */
  const contractRowToDto = (row) => {
    const main = row.mainContractId ? contractStoreOf().find(row.mainContractId) : null;
    return {
      id: row.id,
      ...contractCommon(row),
      response_hours: Number(row.replyHours) || 0,
      on_site_hours: Number(row.arriveHours) || 0,
      completion_hours: Number(row.fixHours) || 0,
      contact_person: row.contact || null,
      contact_phone: row.phone?.number || null,
      contact_country_code: row.phone?.country || null,
      contact_area_code: row.phone?.area || null,
      contact_extension: row.phone?.ext || null,
      contact_email: row.email || null,
      currency_code: row.currency || "TWD",
      description: row.content || null,
      primary_related_contract_id: row.mainContractId != null ? Number(row.mainContractId) : null,
      primary_related_contract_name: main ? `${main.name}（${main.code}）` : null,
      created_on_utc: row.createdOn || "2026-04-08T01:00:00Z",
      updated_on_utc: row.updatedOn || "2026-08-10T02:12:00Z",
    };
  };
  const CONTRACT_RELATION_NAME = { Predecessor: "前一案", Successor: "下一案" };
  /** DcContractRelationDto(11 鍵,照 swagger;SIT 無樣本) */
  const contractRelationDto = (row, rel) => {
    const related = contractStoreOf().find(rel.relatedId);
    return {
      id: rel.id,
      contract_id: row.id,
      related_contract_id: Number(rel.relatedId),
      related_contract_no: related ? related.code : null,
      related_contract_name: related ? related.name : null,
      vendor_name: related ? related.vendorName || null : null,
      start_date: related ? isoDate(related.startDate) : null,
      end_date: related ? isoDate(related.endDate) : null,
      relation_type: rel.type,
      relation_type_name: CONTRACT_RELATION_NAME[rel.type] || null,
      is_primary: !!rel.isPrimary,
    };
  };
  /** DcContractAssetMappingDto(24 鍵,對 9/13 smoke) */
  const contractMappingDto = (row, ref, asset) => ({
    id: ref.id || 0,
    contract_id: row ? row.id : 0,
    ...linkedAssetKeys(ref, asset, CONTRACT_ASSET_TYPE_NAME),
  });
  const contractMappingsOf = (row) =>
    (row.assets || []).map((ref) => {
      const asset = linkedAssetOf(ref);
      if (!asset) console.warn(`[mock] 合約 ${row.code} 的關聯 ${ref.assetType}#${ref.assetId} 在資產 store 找不到,投影略過`);
      return asset ? contractMappingDto(row, ref, asset) : null;
    }).filter(Boolean);
  /** DcContractChangeLogDto(8 鍵,照 swagger;SIT 無樣本)。store 列同 IP 的 {at,by,kind,text,docNo,result} */
  const contractHistoryToChangeLog = (h, i) => {
    const m = /^(.*?)（([^）]+)）$/.exec(h.by || "");
    return {
      id: i + 1,
      changed_on: h.at ? `${h.at.slice(0, 10)}T${h.at.slice(11, 16) || "00:00"}:00` : null,
      changed_by_name: m ? m[1] : (h.by || null),
      changed_by_employee_no: m ? m[2] : null,
      action: h.kind || null,
      description: h.text || null,
      request_no: h.docNo || null,
      status_text: h.result || null,
    };
  };
  /** DcContractMaintenanceRecordDto(13 鍵,照 swagger;SIT 無樣本) */
  // 派工示意頁(DispatchPage)自己加的維護時程是月曆事件形狀 {title, date, color}:投影時也吃得下
  const contractMaintenanceDto = (row, rec, i) => ({
    id: rec.id || i + 1,
    contract_id: row.id,
    sequence: rec.sequence != null ? rec.sequence : i + 1,
    scheduled_on: rec.scheduledOn || (rec.date ? `${rec.date}T00:00:00` : null),
    status_id: Number(rec.statusId) || 0,
    status_name: Number(rec.statusId) === 1 ? "已完成" : "未完成",
    attachment_download_ids: null,
    attachments: (rec.attachments || []).map((a, i) => ({ id: i + 1, download_id: a.downloadId, file_name: a.fileName, download_url: a.downloadUrl })),
    reminder14_days: !!rec.reminder14,
    reminder7_days: !!rec.reminder7,
    reminder1_day: !!rec.reminder1,
    description: rec.description || rec.title || null,
    created_on: rec.createdOn || "2026-04-08T09:00:00",
  });
  const contractIdOf = (config) => Number(config.url.split("?")[0].split("/").pop());

  mock.onGet(`${url.DCK_BACKEND.CONTRACT}/GetAll`).reply(withAuth((config) => {
    const p = config.params || {};
    const kw = String(p.keyword || "").trim().toLowerCase();
    let rows = contractStoreOf().all();
    // query 語意照 swagger:0 = 不限制;日期區間比 YYYY-MM-DD
    if (Number(p.serviceLevelId)) rows = rows.filter((r) => lookupId(SERVICE_LEVELS, r.sla) === Number(p.serviceLevelId));
    if (Number(p.managerCustomerId)) rows = rows.filter((r) => String(r.managerId) === String(p.managerCustomerId));
    if (Number(p.vendorId)) rows = rows.filter((r) => vendorIdOf(r.vendorName) === Number(p.vendorId));
    if (p.startFrom) rows = rows.filter((r) => (r.startDate || "") >= dateOnlyOf(p.startFrom));
    if (p.startTo) rows = rows.filter((r) => (r.startDate || "") <= dateOnlyOf(p.startTo));
    if (p.endFrom) rows = rows.filter((r) => (r.endDate || "") >= dateOnlyOf(p.endFrom));
    if (p.endTo) rows = rows.filter((r) => (r.endDate || "") <= dateOnlyOf(p.endTo));
    if (kw) {
      // 稿的搜尋框:合約編號、合約名稱、廠商
      rows = rows.filter((r) => [r.code, r.name, r.vendorName].some((v) => String(v ?? "").toLowerCase().includes(kw)));
    }
    const pageIndex = Number(p.pageIndex) || 0;
    const pageSize = Number(p.pageSize) || 20;
    return [200, toPageEnvelope(rows.map(contractRowToListItem), pageIndex, pageSize)];
  }));

  mock.onGet(new RegExp(`${url.DCK_BACKEND.CONTRACT}/GetById/\\d+$`)).reply(withAuth((config) => {
    const row = contractStoreOf().find(contractIdOf(config));
    if (!row) return [404, "查無合約資訊"];
    return [200, contractRowToDto(row)];
  }));

  mock.onGet(new RegExp(`${url.DCK_BACKEND.CONTRACT}/GetRelations/\\d+$`)).reply(withAuth((config) => {
    const row = contractStoreOf().find(contractIdOf(config));
    if (!row) return [404, "查無合約資訊"];
    return [200, (row.relations || []).map((rel) => contractRelationDto(row, rel))];
  }));

  mock.onGet(new RegExp(`${url.DCK_BACKEND.CONTRACT}/GetAssetMappings/\\d+$`)).reply(withAuth((config) => {
    const row = contractStoreOf().find(contractIdOf(config));
    if (!row) return [404, "查無合約資訊"];
    return [200, contractMappingsOf(row)];
  }));

  mock.onGet(`${url.DCK_BACKEND.CONTRACT}/GetChangeHistory`).reply(withAuth((config) => {
    const id = Number((config.params || {}).contractId);
    if (!id) return [400, ["contractId 為必填"]];
    const row = contractStoreOf().find(id);
    if (!row) return [404, "查無合約資訊"];
    return [200, (row.history || []).map(contractHistoryToChangeLog)];
  }));

  mock.onGet(new RegExp(`${url.DCK_BACKEND.CONTRACT}/GetMaintenanceRecords/\\d+$`)).reply(withAuth((config) => {
    const row = contractStoreOf().find(contractIdOf(config));
    if (!row) return [404, "查無合約資訊"];
    const p = config.params || {};
    let recs = row.maintenance || [];
    if (p.scheduledFrom) recs = recs.filter((r) => dateOnlyOf(r.scheduledOn) >= dateOnlyOf(p.scheduledFrom));
    if (p.scheduledTo) recs = recs.filter((r) => dateOnlyOf(r.scheduledOn) <= dateOnlyOf(p.scheduledTo));
    return [200, recs.map((rec, i) => contractMaintenanceDto(row, rec, i))];
  }));

  // 可掛的資產＝硬體＋機櫃＋軟體(ContractAssetType;All 或不帶＝全部);沒有 contractId,結果 id／contract_id 為 0
  mock.onGet(`${url.DCK_BACKEND.CONTRACT}/SearchLinkableAssets`).reply(withAuth((config) => {
    const p = config.params || {};
    const hit = (val, needle) => !needle || String(val || "").toLowerCase().includes(String(needle).toLowerCase());
    const type = p.assetType && p.assetType !== "All" ? String(p.assetType) : null;
    const refs = [
      ...(!type || type === "Hardware" ? getResourceStore("hardware").all().map((h) => ({ assetType: "Hardware", assetId: h.id })) : []),
      ...(!type || type === "Rack" ? cabinetStoreOf().all().map((c) => ({ assetType: "Rack", assetId: c.id })) : []),
      ...(!type || type === "Software" ? (getResourceStore("software")?.all() || []).map((sw) => ({ assetType: "Software", assetId: sw.id })) : []),
    ];
    const out = refs
      .map((ref) => ({ ref, asset: linkedAssetOf(ref) }))
      .filter(({ asset }) => asset)
      .filter(({ asset }) => hit(`${asset.name} ${asset.code}`, p.assetName))
      .filter(({ ref, asset }) => !Number(p.roomId) || String(linkedAssetLocation(ref, asset).room?.id) === String(p.roomId))
      .filter(({ asset }) => hit(asset.brand, p.brand))
      .filter(({ asset }) => hit(asset.model, p.model))
      .filter(({ asset }) => !Number(p.managerCustomerId) || String(asset.managerId) === String(p.managerCustomerId))
      .filter(({ ref, asset }) => !p.ip || (ref.assetType === "Hardware" && hit(asset.ip, p.ip)))
      .map(({ ref, asset }) => contractMappingDto(null, { ...ref, id: 0 }, asset));
    return [200, out];
  }));

  // —— DcHardware(DCK-05-02,票 21):投影自共用 hardware store ——
  // 契約依 8/17 規格書(**文件層**,8/12 runtime 仍 500;跑得動時以 smoke 實測為準)。
  //
  // 兩個刻意的決定:
  //
  // 1. **`status_name` 吐後端寫的「運作中」,不是前端在用的「運行中」。**
  //    投影層若「順手」改成前端的字,那條 alias 就永遠不會被執行到 ——
  //    hybrid 一接上真後端才發現整欄掉色。mock 要像後端,不要像前端。
  //
  // 2. **`*_risk_level` 在投影時算完再吐出來**,與 `used_u` 同一個道理(裁示 8/14)。
  //    ⚠️ 下面的天數門檻是 **mock 自己編的** —— 後端的「依剩餘天數換算」規則
  //    沒有寫在 swagger 裡(票 21 要問)。這不違反 ADR-0007:那條禁的是
  //    **前端**自己算一套,而 mock 在這裡扮演的是後端。前端(頁面/helpers)
  //    仍然只讀不算,所以真規則回來時只要改這裡,畫面一行都不用動。
  const hardwareStoreOf = () => getResourceStore("hardware");
  const decorateHardware = decorateManager("hardware");

  const DAY = 24 * 60 * 60 * 1000;
  const riskLevelByDate = (dateStr) => {
    if (!dateStr) return null; // 沒有日期 = 沒有這個風險,不是「低風險」
    const days = Math.floor((new Date(dateStr) - Date.now()) / DAY);
    if (days <= 90) return "嚴重"; // 含已過期(負數)
    if (days <= 180) return "高";
    if (days <= 365) return "中";
    if (days <= 730) return "低";
    return null;
  };

  const hardwareRowToDto = (raw) => {
    const row = decorateHardware(raw);
    const rack = getResourceStore("cabinet").find(Number(row.cabinetId)) || {};
    const room = roomStore().find(Number(rack.roomId)) || {};
    return {
      id: row.id,
      asset_no: row.code || "",
      hardware_no: row.serialNo || "",
      host_name: row.name || "",
      is_key_business: !!row.starred,
      brand: row.brand || "",
      model: row.model || "",
      room_id: Number(rack.roomId) || 0,
      room_name: room.name || "",
      rack_id: Number(row.cabinetId) || 0,
      rack_no: rack.code || "",
      // 8/18 實測格式是 "U12-13"(帶 U 前綴,區間就寫在這個欄位裡)——
      // mock 要吐一樣的格式,否則 parseUPosition 那條路在 mock 模式下永遠走不到
      position_u: formatUPosition(row.uPosition, row.uSize),
      ip_address: row.ip || "",
      eos_date: row.eos || null,
      eos_risk_level: riskLevelByDate(row.eos),
      eol_date: row.eol || null,
      eol_risk_level: riskLevelByDate(row.eol),
      // 種子只有等級沒有筆數 —— 有等級就當作命中 1 筆,沒等級是 0(掃過沒命中)
      cve_risk_level: row.cveLevel || null,
      cve_count: row.cveLevel ? 1 : 0,
      cve_date: row.cveDate || null,
      kev_risk_level: row.kevLevel || null,
      kev_count: row.kevLevel ? 1 : 0,
      kev_date: row.kevDate || null,
      manager_customer_id: Number(row.managerId) || 0,
      manager_customer_name: row.manager || "",
      // 後端的字(見上方決定 1):運作中／維修中／已停用
      status_name: row.status === "維修中" ? "維修中"
        : row.status === "停用" ? "已停用" : "運作中",
      is_under_review: row.approvalStatus === "pending",
      approval_request_no: row.docNo || "",
      /**
       * ⚠️ 契約外(mock-only):以下欄位 `DcHardwareListItemDto` **沒有**,
       * 但 WF／設計稿要求的功能靠它們(與 DcRack 的 `status` 同一種處理)。
       *
       * 用**前端的欄位名**吐出來是刻意的:`hardwareToView` 會 `...h` 原樣帶過,
       * 頁面照舊讀得到;hybrid 接上真後端時這些鍵直接缺席 → 對應的功能自動降級
       * (上架階段欄不顯示、替代機標記不出現、類別篩選會吵一聲),
       * 而不是顯示假資料。缺口逐項記在票 21:
       *
       *   lifecycle      兩段式新增的「草稿 → 已核准待上架 → 已上架」
       *   isSpare/spareCode  維修中的替代機(260804 issue 10)
       *   assetType      資產類別(看板下鑽 ?category=;GetAll 也沒有這個查詢參數)
       *   bizType        業務類別(列表不顯示,但風險面板要)
       *   uSize          佔用 U 數(列表要畫 U 區間;DTO 只有詳情頁的 occupied_u)
       *   parentIp/secondaryIps/subnetMask/gateway/dns/iface  IP 分頁
       */
      // business_category_id 用同一份主檔的序位(見 Common/BusinessCategories)——
      // 篩選送的是 id,投影不給 id 的話那個條件永遠篩不到東西
      business_category_id: BIZ_TYPES.indexOf(row.bizType) + 1 || 0,
      lifecycle: row.lifecycle || undefined,
      isSpare: !!row.isSpare,
      spareCode: row.spareCode || "",
      assetType: row.assetType || "",
      bizType: row.bizType || "",
      uSize: Number(row.uSize) || 1,
      parentIp: row.parentIp || "",
      secondaryIps: row.secondaryIps || [],
      subnetMask: row.subnetMask || "",
      gateway: row.gateway || "",
      dns: row.dns || "",
      iface: row.iface || "",
      mac_address: row.mac || "",
      status: row.status || "運行中",
      docNo: row.docNo || "",
      approvalStatus: row.approvalStatus || undefined,
      amortEndDate: row.amortEndDate || "", // WF_v3 票 21(契約外 mock-only)
    };
  };

  /** GetById 才有的 DcHardwareDto 欄位 —— 列表投影故意不含這些，編輯必須打這支才填得滿 */
  /** 硬體的作業系統／應用系統關聯:`*_system_ids[]`(軟體 Id)＋唯讀 `*_systems[]`(DcSoftwareRefDto) */
  const softwareRelation = (kind, ids, legacyText) => {
    if (Array.isArray(ids) && ids.length) {
      const all = getResourceStore("software").all();
      const refs = ids.map((id) => all.find((s) => String(s.id) === String(id))).filter(Boolean)
        .map((s) => ({ id: s.id, software_no: s.serialNo || "", name: s.name || "", version: s.version || "" }));
      return { [`${kind}_systems`]: refs, [`${kind}_system_ids`]: refs.map((r) => r.id) };
    }
    // 種子的舊字串沒有對應軟體:只給名稱、id 0,不編號 —— 編 1、2… 會撞到真軟體 id,編輯時被誤選
    const names = String(legacyText || "").split(/[、,，]/).map((x) => x.trim()).filter(Boolean);
    return {
      [`${kind}_systems`]: names.map((name) => ({ id: 0, software_no: "", name, version: "" })),
      [`${kind}_system_ids`]: [],
    };
  };

  const hardwareDetailDto = (raw) => {
    const row = decorateHardware(raw);
    return {
      ...hardwareRowToDto(raw),
      firmware_version: row.firmwareVersion || "",
      occupied_u: Number(row.uSize) || 0,
      interface_count: Number(row.interfaceCount) || 0,
      power_count: Number(row.powerCount) || 0,
      cpu_core_count: Number(row.cpuCores) || 0,
      ram_gb: Number(row.ramGb) || 0,
      disk_capacity_tb: Number(row.storageTb) || 0,
      operating_system: row.os || "",
      remark: row.description || row.remark || "",
      property_no: row.propertyNo || "",
      brand_id: Number(row.brandId) || 0,
      model_id: Number(row.modelId) || 0,
      purchase_date: row.purchaseDate || null,
      purchase_amount: Number(row.purchaseAmount) || 0,
      currency_code: row.currency || "TWD",
      mounted_date: row.mountDate || null,
      virtual_brand: row.vmBrand || "",
      virtual_machine_count: Number(row.vmCount) || 0,
      virtual_cpu_core_count: Number(row.deployCpuCores) || 0,
      virtual_ram_gb: Number(row.deployRamGb) || 0,
      virtual_disk_capacity_tb: Number(row.deployStorageTb) || 0,
      application_system: row.appSystems || "",
      vulnerability_scan_date: (row.scan && row.scan.date) || null,
      next_vulnerability_scan_date: (row.scan && row.scan.nextDate) || null,
      change_log: row.changeLog || [],
      // 命中明細(票 uat-round3/01):真後端 GetById 的形狀(2026-09-07 smoke,46 筆),
      // 種子直接以 DTO 形狀存,這裡原樣吐;沒種的資產給空陣列(掃過沒命中),不給 undefined
      cve_date: row.cveDate || null,
      kev_date: row.kevDate || null,
      vulnerability_hits: row.vulnerabilityHits || [],
      // SIT 2026-09-15 GetById 實測有 cpe(swagger DcHardwareDto 也有);沒種就 null
      cpe: row.cpe || null,
      // —— 2026-09-08 mock-vs-real(fixture real-shapes.json)實測 GetById 多出的 13 個鍵 ——
      active: row.status !== "停用",
      asset_category_id: ASSET_TYPES.indexOf(row.assetType) + 1 || 0,
      asset_category_name: row.assetType || "",
      business_category_name: row.bizType || "",
      created_on: row.createdOn || "2026-08-06T23:36:47+08:00",
      updated_on: row.updatedOn || "2026-08-30T18:19:08+08:00",
      end_of_sale_date: row.endOfSale || null,
      latest_version: row.latestVersion || null,
      vulnerability_scan_file_url: (row.scan && row.scan.files && row.scan.files[0] && row.scan.files[0].url) || null,
      // ⚠️ 契約漂移:實測 DTO **沒有** operating_system／application_system(單數字串),
      // 改為多值 `operating_systems[]`／`application_systems[]`({ id, software_no, name, version })
      // 與對應的 *_ids[]。前端 hardwareContract 目前仍讀單數欄位 → 接真後端時 OS／應用系統為空,
      // 寫入也送不進去;修正走契約對齊票 .scratch/api-contract-alignment-20260907/issues/01。
      // 上面的單數欄位先留著讓 mock 模式不壞(mock-vs-real 會列為契約外)。
      // 選過軟體(ids)→ 照 DcSoftwareRefDto 從軟體資料查;沒選過才退回種子字串(舊資料的樣子)
      ...softwareRelation("operating", row.operatingSystemIds, row.os),
      ...softwareRelation("application", row.applicationSystemIds, row.appSystems),
    };
  };

  /** DTO → hardware store 的欄位(只收後端會送的那些) */
  const dcHardwarePatch = (dto) => ({
    ...(dto.asset_no !== undefined ? { code: dto.asset_no } : {}),
    ...(dto.host_name !== undefined ? { name: dto.host_name } : {}),
    ...(dto.hardware_no !== undefined ? { serialNo: dto.hardware_no } : {}),
    ...(dto.is_key_business !== undefined ? { starred: !!dto.is_key_business } : {}),
    ...(dto.brand !== undefined ? { brand: dto.brand } : {}),
    ...(dto.model !== undefined ? { model: dto.model } : {}),
    ...(dto.rack_id !== undefined ? { cabinetId: dto.rack_id } : {}),
    ...(dto.position_u !== undefined ? { uPosition: dto.position_u } : {}),
    ...(dto.occupied_u !== undefined ? { uSize: dto.occupied_u } : {}),
    ...(dto.mac_address !== undefined ? { mac: dto.mac_address } : {}),
    ...(dto.manager_customer_id !== undefined ? { managerId: dto.manager_customer_id } : {}),
    ...(dto.manager_customer_name !== undefined ? { manager: dto.manager_customer_name } : {}),
    ...(dto.business_category_name !== undefined ? { bizType: dto.business_category_name } : {}),
    ...(dto.asset_category_name !== undefined ? { assetType: dto.asset_category_name } : {}),
    ...(dto.active !== undefined ? { status: dto.active ? "運行中" : "停用" } : {}),
    // 2026-09-08 契約:作業系統／應用系統是軟體 id 陣列(單數文字欄已移除,前端也不再送)
    ...(dto.operating_system_ids !== undefined ? { operatingSystemIds: dto.operating_system_ids } : {}),
    ...(dto.application_system_ids !== undefined ? { applicationSystemIds: dto.application_system_ids } : {}),
    // ⚠️ 契約外(mock-only):與 hardwareToDto 那一段對稱 —— 真後端沒有這些欄位,
    // mock 收下來才能讓 WF 的 IP 分頁／兩段式新增／替代機在 mock 模式下運作。
    ...(dto.amortEndDate !== undefined ? { amortEndDate: dto.amortEndDate } : {}), // WF_v3 票 21
    ...(dto.ip !== undefined ? { ip: dto.ip } : {}),
    ...(dto.parentIp !== undefined ? { parentIp: dto.parentIp } : {}),
    ...(dto.secondaryIps !== undefined ? { secondaryIps: dto.secondaryIps } : {}),
    ...(dto.subnetMask !== undefined ? { subnetMask: dto.subnetMask } : {}),
    ...(dto.gateway !== undefined ? { gateway: dto.gateway } : {}),
    ...(dto.dns !== undefined ? { dns: dto.dns } : {}),
    ...(dto.iface !== undefined ? { iface: dto.iface } : {}),
    ...(dto.assetType ? { assetType: dto.assetType } : {}),
    ...(dto.bizType ? { bizType: dto.bizType } : {}),
    ...(dto.firmware_version !== undefined ? { firmwareVersion: dto.firmware_version } : {}),
    ...(dto.cpe !== undefined ? { cpe: dto.cpe } : {}),
    ...(dto.interface_count !== undefined ? { interfaceCount: dto.interface_count } : {}),
    ...(dto.power_count !== undefined ? { powerCount: dto.power_count } : {}),
    ...(dto.cpu_core_count !== undefined ? { cpuCores: dto.cpu_core_count } : {}),
    ...(dto.ram_gb !== undefined ? { ramGb: dto.ram_gb } : {}),
    ...(dto.disk_capacity_tb !== undefined ? { storageTb: dto.disk_capacity_tb } : {}),
    ...(dto.operating_system !== undefined ? { os: dto.operating_system } : {}),
    ...(dto.remark !== undefined ? { description: dto.remark } : {}),
    ...(dto.property_no !== undefined ? { propertyNo: dto.property_no } : {}),
    ...(dto.purchase_date !== undefined ? { purchaseDate: dto.purchase_date } : {}),
    ...(dto.purchase_amount !== undefined ? { purchaseAmount: dto.purchase_amount } : {}),
    ...(dto.currency_code !== undefined ? { currency: dto.currency_code } : {}),
    ...(dto.mounted_date !== undefined ? { mountDate: dto.mounted_date } : {}),
    ...(dto.virtual_brand !== undefined ? { vmBrand: dto.virtual_brand } : {}),
    ...(dto.virtual_machine_count !== undefined ? { vmCount: dto.virtual_machine_count } : {}),
    ...(dto.virtual_cpu_core_count !== undefined ? { deployCpuCores: dto.virtual_cpu_core_count } : {}),
    ...(dto.virtual_ram_gb !== undefined ? { deployRamGb: dto.virtual_ram_gb } : {}),
    ...(dto.virtual_disk_capacity_tb !== undefined ? { deployStorageTb: dto.virtual_disk_capacity_tb } : {}),
    ...(dto.application_system !== undefined ? { appSystems: dto.application_system } : {}),
    ...(dto.vulnerability_scan_date !== undefined || dto.next_vulnerability_scan_date !== undefined ? {
      scan: {
        date: dto.vulnerability_scan_date || "",
        nextDate: dto.next_vulnerability_scan_date || "",
        files: [],
        rows: [],
      },
    } : {}),
    ...(dto.lifecycle !== undefined ? { lifecycle: dto.lifecycle } : {}),
    ...(dto.isSpare !== undefined ? { isSpare: dto.isSpare } : {}),
    ...(dto.spareCode !== undefined ? { spareCode: dto.spareCode } : {}),
  });

  mock.onGet(`${url.DCK_BACKEND.HARDWARE}/GetAll`).reply(withAuth((config) => {
    const p = config.params || {};
    // 契約更新:keyword 更名 keywords,可重複傳(keywords=Dell&keywords=VM)。
    // 多筆做**交叉(AND)**:每個關鍵字都要命中「硬體編號、名稱或 IP」其中一欄。
    const kws = asArray(p.keywords).map((s) => String(s).trim().toLowerCase()).filter(Boolean);
    // status 值用前端狀態字(mock-only,見 filterByStatus)
    let rows = filterByStatus(hardwareStoreOf().all().map(decorateHardware), p.status)
      .map(hardwareRowToDto);
    // query 語意照 swagger:0 = 不限制
    if (Number(p.roomId)) rows = rows.filter((r) => r.room_id === Number(p.roomId));
    if (Number(p.rackId)) rows = rows.filter((r) => r.rack_id === Number(p.rackId));
    if (Number(p.managerCustomerId)) {
      rows = rows.filter((r) => r.manager_customer_id === Number(p.managerCustomerId));
    }
    if (kws.length) {
      rows = rows.filter((r) => kws.every((kw) =>
        [r.asset_no, r.hardware_no, r.host_name, r.ip_address]
          .some((v) => String(v ?? "").toLowerCase().includes(kw))));
    }
    // 風險篩選 —— 這一段是**在後端做**的(ADR-0007);mock 也在這裡做,
    // 頁面才不會養成「拿回來再自己過濾一次」的習慣。
    const kinds = asArray(p.riskTypes).map((k) => String(k).toUpperCase());
    if (kinds.length) {
      const within = (dateStr) => {
        if (!dateStr) return false;
        const d = new Date(dateStr);
        if (p.riskStartDate && p.riskEndDate) {
          return d >= new Date(p.riskStartDate) && d <= new Date(p.riskEndDate);
        }
        const months = Number(p.riskMonths) || 0;
        if (!months) return true; // 沒給時間窗 = 只看有沒有這個日期
        const limit = new Date();
        limit.setMonth(limit.getMonth() + months);
        return d <= limit;
      };
      rows = rows.filter((r) => kinds.some((k) => {
        if (k === "EOS") return within(r.eos_date);
        if (k === "EOL") return within(r.eol_date);
        if (k === "CVE") return !!r.cve_risk_level;
        if (k === "KEV") return !!r.kev_risk_level;
        return false;
      }));
    }
    const levelCodes = asArray(p.riskLevels).map(Number).filter(Boolean);
    if (levelCodes.length) {
      const CODE = { 嚴重: 4, 高: 3, 中: 2, 低: 1 };
      rows = rows.filter((r) => [r.eos_risk_level, r.eol_risk_level, r.cve_risk_level, r.kev_risk_level]
        .some((lv) => lv && levelCodes.includes(CODE[lv])));
    }
    const bizIds = asArray(p.businessCategoryIds).map(Number).filter(Boolean);
    if (bizIds.length) rows = rows.filter((r) => bizIds.includes(r.business_category_id));
    if (p.onlyKeyBusiness === true || p.onlyKeyBusiness === "true") {
      rows = rows.filter((r) => r.is_key_business);
    }
    const pageIndex = Number(p.pageIndex) || 0;
    const pageSize = Number(p.pageSize) || 20;
    return [200, toPageEnvelope(rows, pageIndex, pageSize)];
  }));

  mock.onGet(new RegExp(`${url.DCK_BACKEND.HARDWARE}/GetById/\\d+$`)).reply(withAuth((config) => {
    const row = hardwareStoreOf().find(Number(config.url.split("/").pop()));
    if (!row) return [404, "查無硬體資訊"];
    return [200, hardwareDetailDto(row)];
  }));

  mock.onPost(`${url.DCK_BACKEND.HARDWARE}/Create`).reply(withAuth((config) => {
    const dto = JSON.parse(config.data || "{}");
    // swagger 的 required 是空的,但描述寫了必填 —— mock 照描述擋,
    // 免得前端漏驗到正式環境才炸(與 DcRack 同一種處理)
    const errors = [];
    if (!dto.asset_no) errors.push("資產編號為必填");
    if (!dto.host_name) errors.push("資產名稱為必填");
    if (!dto.rack_id) errors.push("機櫃為必填");
    if (!dto.position_u) errors.push("U 位置為必填");
    if (errors.length) return [400, errors];
    const record = hardwareStoreOf().add({ ...dcHardwarePatch(dto), status: "運行中" });
    return [200, hardwareRowToDto(record)];
  }));

  mock.onPut(`${url.DCK_BACKEND.HARDWARE}/Update`).reply(withAuth((config) => {
    const dto = JSON.parse(config.data || "{}");
    if (!hardwareStoreOf().find(dto.id)) return [404, "查無硬體資訊"];
    hardwareStoreOf().patch(dto.id, dcHardwarePatch(dto));
    return [200, hardwareRowToDto(hardwareStoreOf().find(dto.id))];
  }));

  // #30:硬體 CanDelete — 檢查底下有沒有軟體(UAT 要求刪除前確認)
  mock.onGet(new RegExp(`${url.DCK_BACKEND.HARDWARE}/CanDelete/\\d+$`)).reply(withAuth((config) => {
    const id = Number(config.url.split("/").pop());
    const hw = hardwareStoreOf().find(id);
    if (!hw) return [404, "查無硬體資訊"];
    // 在軟體 store 裡找有沒有軟體 deploy 到這台硬體
    const swStore = getResourceStore("software");
    const linked = swStore ? swStore.all().filter((s) => (s.deployedHardwareIds || []).includes(id)) : [];
    return [200, {
      can_delete: linked.length === 0,
      software_count: linked.length,
      sample_software_nos: linked.slice(0, 3).map((s) => s.code || s.name),
    }];
  }));

  mock.onDelete(new RegExp(`${url.DCK_BACKEND.HARDWARE}/Delete/\\d+$`)).reply(withAuth((config) => {
    const id = Number(config.url.split("/").pop());
    if (!hardwareStoreOf().find(id)) return [404, "查無硬體資訊"];
    hardwareStoreOf().remove(id);
    return [200, ""];
  }));

  // —— DcSoftware(DCK-05-03,票 35):投影自共用 software store ——
  // GetAll 形狀抄 8/18 smoke。deployments[] 帶在列表列（Q2 路徑 a）。
  // AddDeployment／RemoveDeployment 是 GET 寫入 —— mock 實作是為了 helper 測路徑,
  // 業務流程先不要打（Q1）。
  const softwareStoreOf = () => getResourceStore("software");
  const decorateSoftware = decorateManager("software");
  const ENV_IDS = { 營運: 1, 測試: 2, 開發: 3, 備援: 4 };

  const softwareDeploymentsOf = (row) => (row.deployedHardwareIds || [])
    .map((hid) => {
      const hw = hardwareStoreOf().find(Number(hid));
      if (!hw) return null;
      const dto = hardwareRowToDto(hw);
      return {
        hardware_id: dto.id,
        asset_no: dto.asset_no,
        host_name: dto.host_name,
        room_name: dto.room_name,
        rack_no: dto.rack_no,
        position_u: dto.position_u,
        manager_customer_name: dto.manager_customer_name,
        eos_date: dto.eos_date,
        // 契約有、原本漏投影(票 39):剩餘天數由後端算,mock 依 eos_date 對今天算(可能為負=已過期)。
        // ⚠️ 近似值:後端的算法與時區未知,且這裡 eos_date 以 UTC 解析、今天是本地時區,跨午夜可能差一天。
        // 畫面只用它排序與標色,差一天不影響;要精確得等後端給算法。
        eos_remaining_days: dto.eos_date
          ? Math.ceil((new Date(dto.eos_date) - new Date()) / 86400000)
          : null,
        cve_count: dto.cve_count || 0,
        cve_risk_level: dto.cve_risk_level,
        is_key_business: dto.is_key_business,
      };
    })
    .filter(Boolean);

  const softwareRowToDto = (raw) => {
    const row = decorateSoftware(raw);
    const deployments = softwareDeploymentsOf(row);
    return {
      id: row.id,
      asset_no: row.code || "",
      software_no: row.serialNo || "",
      host_name: row.name || "",
      version: row.version || "",
      is_key_business: !!row.starred,
      brand: row.brand || "",
      model: row.model || "",
      environment_name: row.env || "",
      eos_date: row.eos || null,
      eos_risk_level: riskLevelByDate(row.eos),
      eol_date: row.eol || null,
      eol_risk_level: riskLevelByDate(row.eol),
      cve_risk_level: row.cveLevel || null,
      cve_count: row.cveLevel ? (row.vulnerabilityHits || []).length || 1 : 0,
      cve_date: row.cveDate || null,
      kev_risk_level: row.kevLevel || null,
      kev_count: row.kevLevel ? 1 : 0,
      kev_date: row.kevDate || null,
      manager_customer_id: Number(row.managerId) || 0,
      manager_customer_name: row.manager || "",
      is_under_review: row.approvalStatus === "pending",
      approval_request_no: row.docNo || "",
      deployment_count: deployments.length,
      deployments,
      environment_id: ENV_IDS[row.env] || 0,
      business_category_id: BIZ_TYPES.indexOf(row.bizType) + 1 || 0,
      business_category_name: row.bizType || "",
      asset_category_id: ASSET_TYPES.indexOf(row.assetType) + 1 || 0,
      asset_category_name: row.assetType || "",
      /**
       * ⚠️ 契約外(mock-only):列表列沒有這些,但看板 CVE 明細與表單命中清單靠它們。
       * hybrid 接上真後端時這些鍵缺席 → 看板命中表變空(那是實話,GetAll 沒有 hits)。
       * 內容是後端 DTO 形狀(票 39),與 GetById 的 `vulnerability_hits` 同一份。
       */
      cveHits: row.vulnerabilityHits || [],
    };
  };

  const softwareDetailDto = (raw) => {
    const row = decorateSoftware(raw);
    return {
      ...softwareRowToDto(raw),
      property_no: row.propertyNo || "",
      remark: row.remark || "",
      brand_id: Number(row.brandId) || 0,
      model_id: Number(row.modelId) || 0,
      vendor_name: row.vendorName || "",
      deployed_date: row.deployedDate || null,
      purchase_amount: Number(row.purchaseAmount) || 0,
      currency_code: row.currency || "TWD",
      active: row.status !== "停用",
      created_on: row.createdOn || "2026-08-06T23:36:47+08:00",
      updated_on: row.updatedOn || "2026-08-30T18:19:08+08:00",
      end_of_sale_date: row.endOfSale || null,
      latest_version: row.latestVersion || null,
      cpe: row.cpe || null,
      vulnerability_scan_date: (row.scan && row.scan.date) || null,
      vulnerability_scan_file_url: (row.scan && row.scan.files && row.scan.files[0] && row.scan.files[0].url) || null,
      next_vulnerability_scan_date: (row.scan && row.scan.nextDate) || null,
      penetration_test_date: (row.pentest && row.pentest.date) || null,
      penetration_test_file_url: (row.pentest && row.pentest.files && row.pentest.files[0] && row.pentest.files[0].url) || null,
      next_penetration_test_date: (row.pentest && row.pentest.nextDate) || null,
      change_log: row.changeLog || [],
      vulnerability_hits: row.vulnerabilityHits || [],
      // mock-only:弱掃／滲透明細表與 IP 分頁,契約沒有
      scan: row.scan || { date: "", nextDate: "", files: [], rows: [] },
      pentest: row.pentest || { date: "", nextDate: "", files: [], rows: [] },
      ip: row.ip || "",
      parentIp: row.parentIp || "",
      ports: row.ports || [],
      deployedHardwareIds: row.deployedHardwareIds || [],
    };
  };

  const dcSoftwarePatch = (dto) => ({
    ...(dto.asset_no !== undefined ? { code: dto.asset_no } : {}),
    ...(dto.host_name !== undefined ? { name: dto.host_name } : {}),
    ...(dto.software_no !== undefined ? { serialNo: dto.software_no } : {}),
    ...(dto.version !== undefined ? { version: dto.version } : {}),
    ...(dto.cpe !== undefined ? { cpe: dto.cpe } : {}),
    ...(dto.is_key_business !== undefined ? { starred: !!dto.is_key_business } : {}),
    ...(dto.brand !== undefined ? { brand: dto.brand } : {}),
    ...(dto.model !== undefined ? { model: dto.model } : {}),
    ...(dto.manager_customer_id !== undefined ? { managerId: dto.manager_customer_id } : {}),
    ...(dto.manager_customer_name !== undefined ? { manager: dto.manager_customer_name } : {}),
    ...(dto.property_no !== undefined ? { propertyNo: dto.property_no } : {}),
    ...(dto.remark !== undefined ? { remark: dto.remark } : {}),
    ...(dto.eos_date !== undefined ? { eos: dto.eos_date } : {}),
    ...(dto.eol_date !== undefined ? { eol: dto.eol_date } : {}),
    ...(dto.env !== undefined ? { env: dto.env } : {}),
    ...(dto.environment_id !== undefined && Object.keys(ENV_IDS).find((k) => ENV_IDS[k] === Number(dto.environment_id))
      ? { env: Object.keys(ENV_IDS).find((k) => ENV_IDS[k] === Number(dto.environment_id)) }
      : {}),
    ...(dto.bizType ? { bizType: dto.bizType } : {}),
    ...(dto.assetType ? { assetType: dto.assetType } : {}),
    ...(dto.ip !== undefined ? { ip: dto.ip } : {}),
    ...(dto.parentIp !== undefined ? { parentIp: dto.parentIp } : {}),
    ...(dto.ports !== undefined ? { ports: dto.ports } : {}),
    // 命中清單是**系統產生、唯讀**(swagger:「命中清單（系統產生、唯讀）」),寫入側不收 ——
    // 收下來的話會把畫面形狀的 cveHits 存回列,而投影讀的是 DTO 形狀的 vulnerabilityHits,
    // 於是編輯過的軟體 `vulnerability_hits` 永遠是空的(票 39 review 抓到)。不收＝沿用原本那份。
    ...(dto.scan !== undefined ? { scan: dto.scan } : {}),
    ...(dto.pentest !== undefined ? { pentest: dto.pentest } : {}),
    ...(dto.deployedHardwareIds !== undefined ? { deployedHardwareIds: dto.deployedHardwareIds } : {}),
  });

  mock.onGet(`${url.DCK_BACKEND.SOFTWARE}/GetAll`).reply(withAuth((config) => {
    const p = config.params || {};
    // 契約更新:keyword 更名 keywords,可重複傳;多筆交叉(AND)—— 與硬體 GetAll 同一套
    const kws = asArray(p.keywords).map((s) => String(s).trim().toLowerCase()).filter(Boolean);
    let rows = filterByStatus(softwareStoreOf().all(), p.status).map(softwareRowToDto);
    if (Number(p.managerCustomerId)) {
      rows = rows.filter((r) => r.manager_customer_id === Number(p.managerCustomerId));
    }
    if (p.environmentId !== undefined && p.environmentId !== "" && p.environmentId !== "Null") {
      const envId = Number(p.environmentId) || 0;
      if (envId) {
        rows = rows.filter((r) => r.environment_id === envId);
      }
    }
    if (kws.length) {
      rows = rows.filter((r) => kws.every((kw) =>
        [r.asset_no, r.host_name, r.brand, r.version]
          .some((v) => String(v ?? "").toLowerCase().includes(kw))));
    }
    const kinds = asArray(p.riskTypes).map((k) => String(k).toUpperCase());
    if (kinds.length) {
      const within = (dateStr) => {
        if (!dateStr) return false;
        const d = new Date(dateStr);
        if (p.riskStartDate && p.riskEndDate) {
          return d >= new Date(p.riskStartDate) && d <= new Date(p.riskEndDate);
        }
        const months = Number(p.riskMonths) || 0;
        if (!months) return true;
        const limit = new Date();
        limit.setMonth(limit.getMonth() + months);
        return d <= limit;
      };
      rows = rows.filter((r) => kinds.some((k) => {
        if (k === "EOS") return within(r.eos_date);
        if (k === "EOL") return within(r.eol_date);
        if (k === "CVE") return !!r.cve_risk_level;
        if (k === "KEV") return !!r.kev_risk_level;
        return false;
      }));
    }
    const levelCodes = asArray(p.riskLevels).map(Number).filter(Boolean);
    if (levelCodes.length) {
      const CODE = { 嚴重: 4, 高: 3, 中: 2, 低: 1 };
      rows = rows.filter((r) => [r.eos_risk_level, r.eol_risk_level, r.cve_risk_level, r.kev_risk_level]
        .some((lv) => lv && levelCodes.includes(CODE[lv])));
    }
    const bizIds = asArray(p.businessCategoryIds).map(Number).filter(Boolean);
    if (bizIds.length) rows = rows.filter((r) => bizIds.includes(r.business_category_id));
    if (p.onlyKeyBusiness === true || p.onlyKeyBusiness === "true") {
      rows = rows.filter((r) => r.is_key_business);
    }
    const pageIndex = Number(p.pageIndex) || 0;
    const pageSize = Number(p.pageSize) || 20;
    return [200, toPageEnvelope(rows, pageIndex, pageSize)];
  }));

  mock.onGet(new RegExp(`${url.DCK_BACKEND.SOFTWARE}/GetById/\\d+$`)).reply(withAuth((config) => {
    const row = softwareStoreOf().find(Number(config.url.split("/").pop()));
    if (!row) return [404, "查無軟體資訊"];
    return [200, softwareDetailDto(row)];
  }));

  mock.onPost(`${url.DCK_BACKEND.SOFTWARE}/Create`).reply(withAuth((config) => {
    const dto = JSON.parse(config.data || "{}");
    const errors = [];
    if (!dto.asset_no) errors.push("資產編號為必填");
    if (!dto.host_name) errors.push("資產名稱為必填");
    if (errors.length) return [400, errors];
    const record = softwareStoreOf().add({ ...dcSoftwarePatch(dto), status: "使用中" });
    return [200, softwareDetailDto(record)];
  }));

  mock.onPut(`${url.DCK_BACKEND.SOFTWARE}/Update`).reply(withAuth((config) => {
    const dto = JSON.parse(config.data || "{}");
    if (!softwareStoreOf().find(dto.id)) return [404, "查無軟體資訊"];
    softwareStoreOf().patch(dto.id, dcSoftwarePatch(dto));
    return [200, softwareDetailDto(softwareStoreOf().find(dto.id))];
  }));

  mock.onGet(new RegExp(`${url.DCK_BACKEND.SOFTWARE}/CanDelete/\\d+$`)).reply(withAuth((config) => {
    const row = softwareStoreOf().find(Number(config.url.split("/").pop()));
    if (!row) return [404, "查無軟體資訊"];
    const ids = row.deployedHardwareIds || [];
    // deployment_count 是契約欄位(SoftwareDeletionCheckResultDto),原本漏給(票 39)
    return [200, { can_delete: ids.length === 0, deployment_count: ids.length, sample_hardware_ids: ids.slice(0, 3) }];
  }));

  mock.onDelete(new RegExp(`${url.DCK_BACKEND.SOFTWARE}/Delete/\\d+$`)).reply(withAuth((config) => {
    const id = Number(config.url.split("/").pop());
    if (!softwareStoreOf().find(id)) return [404, "查無軟體資訊"];
    softwareStoreOf().remove(id);
    return [200, ""];
  }));

  mock.onGet(new RegExp(`${url.DCK_BACKEND.SOFTWARE}/GetDeployments/\\d+$`)).reply(withAuth((config) => {
    const row = softwareStoreOf().find(Number(config.url.split("/").pop()));
    if (!row) return [404, "查無軟體資訊"];
    return [200, (row.deployedHardwareIds || []).map((hardware_id, i) => ({
      id: i + 1, software_id: row.id, hardware_id,
    }))];
  }));

  mock.onGet(new RegExp(`${url.DCK_BACKEND.SOFTWARE}/AddDeployment/\\d+/\\d+`)).reply(withAuth((config) => {
    const parts = String(config.url).split("?")[0].split("/").filter(Boolean);
    const hardwareId = Number(parts.pop());
    const softwareId = Number(parts.pop());
    const row = softwareStoreOf().find(softwareId);
    if (!row) return [404, "查無軟體資訊"];
    const ids = row.deployedHardwareIds || [];
    if (!ids.map(Number).includes(hardwareId)) {
      softwareStoreOf().patch(softwareId, { deployedHardwareIds: [...ids, hardwareId] });
    }
    return [200, ""];
  }));

  mock.onGet(new RegExp(`${url.DCK_BACKEND.SOFTWARE}/RemoveDeployment/\\d+/\\d+`)).reply(withAuth((config) => {
    const parts = String(config.url).split("?")[0].split("/").filter(Boolean);
    const hardwareId = Number(parts.pop());
    const softwareId = Number(parts.pop());
    const row = softwareStoreOf().find(softwareId);
    if (!row) return [404, "查無軟體資訊"];
    softwareStoreOf().patch(softwareId, {
      deployedHardwareIds: (row.deployedHardwareIds || []).filter((id) => Number(id) !== hardwareId),
    });
    return [200, ""];
  }));

  // —— Common 三支清單(票 27)——
  // 形狀刻意不同,照 swagger:Currencies 是 { name, currency_code },
  // 兩個 Categories 是 { value:int, text }(schema 名 DcRackOption,兩支共用)。
  // 這裡的 id 就是「後端那份 id」的 mock 代表:前端表單存 id、顯示 text,
  // 不再把中文名當主鍵(票 20 的 business_category_id 送 0 就是這麼來的)。
  mock.onGet(`${url.DCK_BACKEND.COMMON}/Currencies`).reply(withAuth(() => [200, [
    { name: "新臺幣", currency_code: "TWD" },
    { name: "美元", currency_code: "USD" },
    { name: "日圓", currency_code: "JPY" },
  ]]));
  mock.onGet(`${url.DCK_BACKEND.COMMON}/BusinessCategories`).reply(withAuth(() => [200,
    BIZ_TYPES.map((text, i) => ({ value: i + 1, text })),
  ]));
  mock.onGet(`${url.DCK_BACKEND.COMMON}/AssetCategories`).reply(withAuth(() => [200,
    ASSET_TYPES.map((text, i) => ({ value: i + 1, text })),
  ]));

  // Common 新增兩支(2026-08-17 規格書)—— 都回 DcAssetOption { value:int, text }。
  // 營運環境的 value 對應 DcOperatingEnvironment enum 的序位(1營運/2測試/3開發/4備援);
  // 風險等級照文件註記 4嚴重/3高/2中/1低 —— 注意**數字大的是嚴重**,不要反過來。
  mock.onGet(`${url.DCK_BACKEND.COMMON}/OperatingEnvironments`).reply(withAuth(() => [200, [
    { value: 1, text: "營運" },
    { value: 2, text: "測試" },
    { value: 3, text: "開發" },
    { value: 4, text: "備援" },
  ]]));
  mock.onGet(`${url.DCK_BACKEND.COMMON}/RiskLevels`).reply(withAuth(() => [200, [
    { value: 4, text: "嚴重" },
    { value: 3, text: "高" },
    { value: 2, text: "中" },
    { value: 1, text: "低" },
  ]]));
  // 合約主檔兩支(票 27;值抄 SIT 9/13 實測,見 db/contractLookups.js)
  mock.onGet(`${url.DCK_BACKEND.COMMON}/ServiceLevels`).reply(withAuth(() => [200, SERVICE_LEVELS]));
  mock.onGet(`${url.DCK_BACKEND.COMMON}/MaintenanceCycles`).reply(withAuth(() => [200, MAINTENANCE_CYCLES]));

  // DcBrand — 廠牌／型號主檔(票 32)。**唯讀**,不提供 Create/Update/Delete:
  // mock 也不給,免得有人寫了一個真後端沒有的寫入路徑。
  const BRANDS = [
    { id: 1, name: "Cisco", code: "CSCO", note: "網路設備", active: true, model_count: 2 },
    { id: 2, name: "APC", code: "APC", note: "機櫃／PDU", active: true, model_count: 1 },
    { id: 3, name: "HPE", code: "HPE", note: "伺服器", active: true, model_count: 1 },
    { id: 4, name: "VMware", code: "VMW", note: "虛擬化軟體", active: true, model_count: 1 },
  ];
  const MODELS = [
    {
      id: 11, brand_id: 1, brand_name: "Cisco", name: "Catalyst 9500",
      asset_type: "Hardware", category: "交換器", interface_count: 48,
      firmware_version: "IOS XE 17.9", occupied_u: 1, total_u: 0, power_count: 2,
      cpu_core_count: 0, ram_gb: 0, disk_capacity_tb: 0, operating_system: "IOS XE",
      eos_date: "2027-06-30T00:00:00", eol_date: "2029-06-30T00:00:00",
      end_of_sale_date: "2026-12-31T00:00:00", latest_version: "17.12.3",
      source_url: "https://cisco.example/eox", data_source: "cisco-csaf",
      last_scraped_on_utc: "2026-08-16T02:00:00Z", note: "", active: true,
    },
    {
      id: 12, brand_id: 1, brand_name: "Cisco", name: "Nexus 9300",
      asset_type: "Hardware", category: "交換器", interface_count: 54,
      firmware_version: "NX-OS 10.2", occupied_u: 1, total_u: 0, power_count: 2,
      cpu_core_count: 0, ram_gb: 0, disk_capacity_tb: 0, operating_system: "NX-OS",
      eos_date: null, eol_date: null, end_of_sale_date: null, latest_version: "10.3.5",
      source_url: "", data_source: "", last_scraped_on_utc: null, note: "", active: true,
    },
    {
      id: 21, brand_id: 2, brand_name: "APC", name: "AR3100",
      asset_type: "Rack", category: "機櫃", interface_count: 0, firmware_version: "",
      occupied_u: 0, total_u: 42, power_count: 2, cpu_core_count: 0, ram_gb: 0,
      disk_capacity_tb: 0, operating_system: "", eos_date: null, eol_date: null,
      end_of_sale_date: null, latest_version: "", source_url: "", data_source: "",
      last_scraped_on_utc: null, note: "", active: true,
    },
    {
      id: 31, brand_id: 3, brand_name: "HPE", name: "ProLiant DL380",
      asset_type: "Hardware", category: "伺服器", interface_count: 4,
      firmware_version: "iLO 5", occupied_u: 2, total_u: 0, power_count: 2,
      cpu_core_count: 32, ram_gb: 256, disk_capacity_tb: 12,
      operating_system: "Windows Server 2019",
      eos_date: "2026-10-15T00:00:00", eol_date: "2028-10-15T00:00:00",
      end_of_sale_date: null, latest_version: "2.90", source_url: "",
      data_source: "", last_scraped_on_utc: null, note: "", active: true,
    },
    {
      id: 41, brand_id: 4, brand_name: "VMware", name: "vCenter Server",
      asset_type: "Software", category: "虛擬化", interface_count: 0,
      firmware_version: "", occupied_u: 0, total_u: 0, power_count: 0,
      cpu_core_count: 0, ram_gb: 0, disk_capacity_tb: 0, operating_system: "",
      eos_date: "2027-10-31T00:00:00", eol_date: "2029-10-31T00:00:00",
      end_of_sale_date: null, latest_version: "8.0 U3", source_url: "",
      data_source: "", last_scraped_on_utc: null, note: "", active: true,
    },
  ];
  const withTimestamps = (row) => ({
    ...row,
    created_on: "2026-01-01T00:00:00", updated_on: "2026-08-01T00:00:00",
  });
  // assetType 是**用途過濾**:查機櫃能用的型號就傳 Rack。廠牌那支則是
  // 「有這種型號的廠牌」—— 兩支的語意不一樣,不要共用一個 filter。
  const typeMatches = (want, actual) => !want || want === "All" || want === actual;

  mock.onGet(`${url.DCK_BACKEND.BRAND}/GetAll`).reply(withAuth((config) => {
    const p = config.params || {};
    const kw = String(p.keyword || "").trim().toLowerCase();
    let list = BRANDS.filter((b) => (p.active === undefined ? true : b.active === p.active));
    if (p.assetType && p.assetType !== "All") {
      const brandIds = new Set(MODELS.filter((m) => m.asset_type === p.assetType).map((m) => m.brand_id));
      list = list.filter((b) => brandIds.has(b.id));
    }
    if (kw) {
      list = list.filter((b) =>
        `${b.name} ${b.code}`.toLowerCase().includes(kw));
    }
    return [200, toPageEnvelope(list.map(withTimestamps),
      Number(p.pageIndex || 0), Number(p.pageSize || 2147483647))];
  }));
  mock.onGet(new RegExp(`${url.DCK_BACKEND.BRAND}/GetById/\\d+$`)).reply(withAuth((config) => {
    const id = Number(config.url.split("/").pop());
    const hit = BRANDS.find((b) => b.id === id);
    return hit ? [200, withTimestamps(hit)] : [404, "找不到廠牌"];
  }));
  mock.onGet(`${url.DCK_BACKEND.BRAND}/GetModels`).reply(withAuth((config) => {
    const p = config.params || {};
    const kw = String(p.keyword || "").trim().toLowerCase();
    let list = MODELS.filter((m) => (p.active === undefined ? true : m.active === p.active));
    if (p.brandId) list = list.filter((m) => m.brand_id === Number(p.brandId));
    list = list.filter((m) => typeMatches(p.assetType, m.asset_type));
    if (kw) list = list.filter((m) => `${m.name} ${m.category}`.toLowerCase().includes(kw));
    return [200, toPageEnvelope(list.map(withTimestamps),
      Number(p.pageIndex || 0), Number(p.pageSize || 2147483647))];
  }));
  mock.onGet(new RegExp(`${url.DCK_BACKEND.BRAND}/GetModelById/\\d+$`)).reply(withAuth((config) => {
    const id = Number(config.url.split("/").pop());
    const hit = MODELS.find((m) => m.id === id);
    return hit ? [200, withTimestamps(hit)] : [404, "找不到型號"];
  }));

  // AssetVersion/GetRecentRuns(票 29 第二個缺口)—— 8/17 規格書補上 DcScraperRunDto。
  // success 是 **boolean**(不是狀態字串);items_failed>0 但 success=true 是可能的,
  // 所以種子刻意留一筆「跑成功但有 2 筆沒對上」,那條分支才 demo 得出來。
  mock.onGet(`${url.DCK_BACKEND.ASSET_VERSION}/GetRecentRuns`).reply(withAuth((config) => {
    const p = config.params || {};
    const runs = [
      {
        id: 3, source: "cisco-csaf+cisa-kev",
        started_on_utc: "2026-08-16T02:00:00Z", finished_on_utc: "2026-08-16T02:04:12Z",
        success: true, items_processed: 128, items_updated: 12, items_failed: 2,
        message: "", created_on_utc: "2026-08-16T02:00:00Z",
      },
      {
        id: 2, source: "nvd",
        started_on_utc: "2026-08-15T02:00:00Z", finished_on_utc: "2026-08-15T02:03:40Z",
        success: true, items_processed: 96, items_updated: 4, items_failed: 0,
        message: "", created_on_utc: "2026-08-15T02:00:00Z",
      },
    ];
    const list = p.source ? runs.filter((r) => r.source === p.source) : runs;
    return [200, toPageEnvelope(list, Number(p.pageIndex || 0), Number(p.pageSize || 50))];
  }));

  // DcCustomerRole/GetPermissionSchema(票 19)—— 角色編輯畫面的版面資料。
  // 形狀照 swagger v1.00 的 RolePermissionSchemaDto;三態文字用稿上的
  // 「編輯／唯讀／隱藏」(ButtonGroup 三顆)。這裡刻意**逐模組**給 access_options,
  // 因為後端契約就是逐模組給的 —— 若哪天有模組不給「唯讀」,前端要吃得下。
  mock.onGet(`${url.DCK_BACKEND.CUSTOMER_ROLE}/GetPermissionSchema`).reply(withAuth(() => {
    const ACCESS_OPTIONS = [
      { text: "編輯", value: "Edit" },
      { text: "唯讀", value: "View" },
      { text: "隱藏", value: "Hidden" },
    ];
    const MODULES = [
      ["People", "01", "人員管理"],
      ["Dashboard", "02", "智慧看板"],
      ["Room", "03", "機房管理"],
      ["Registry", "04", "營運資訊（IP／專案／合約／廠商）"],
      ["Asset", "05", "資產管理"],
      ["Monitor", "06", "監控派工"],
      ["Notify", "07", "訊息功能"],
      ["Report", "08", "報表類型"],
      ["Decision", "09", "決策分析"],
      ["Approval", "", "審核功能（三關簽核）"],
      ["Import", "", "批次匯入（各模組）"],
    ];
    return [200, {
      modules: MODULES.map(([module_key, code, name]) => ({
        module_key, code, name, access_options: ACCESS_OPTIONS,
      })),
      // field_key 照契約是 **PascalCase**(swagger 註明「如 ContractAmount」)。
      // 原本這裡寫成 camelCase —— 那是照前端的 masks 寫的,不是照契約寫的,
      // 剛好把「欄位級開關存不進去」那個 bug 遮住(mp-code-review Spec #1)。
      fields: [
        { field_key: "ContractAmount", name: "合約管理－合約金額" },
        { field_key: "AssetAmount", name: "資產管理－購買金額" },
        { field_key: "ReportAmount", name: "報表功能－購買金額" },
        { field_key: "DecisionAmount", name: "決策分析－金額數據" },
        // WF_v3 新增(票 21):訊息暫停送審限 Overseer。swagger v1.00 沒有
        // 這個 field_key —— 依設計稿先行,已列入回報後端清單。
        { field_key: "NotifyPause", name: "訊息設定－暫停" },
      ],
      // 範本:目前前端的「以範本建立」讀的是既有角色清單(比罐頭範本實用),
      // 這裡照契約回,但畫面尚未使用 —— 形狀先對上,之後要用不必再改 mock。
      templates: [],
    }];
  }));

  // AssetVersion/SuggestCpe —— NVD CPE 字典候選。⚠️ 形狀照 swagger CpeSuggestionDto,
  // 尚未 smoke 實測(沙箱連不到 SIT);實測後照 ADR-0004 抄回來。
  const CPE_DICTIONARY = [
    { cpe_name: "cpe:2.3:o:cisco:ios_xe:17.9.1:*:*:*:*:*:*:*", title: "Cisco IOS XE 17.9.1", deprecated: false },
    { cpe_name: "cpe:2.3:o:cisco:nx-os:10.2\\(3\\):*:*:*:*:*:*:*", title: "Cisco NX-OS 10.2(3)", deprecated: false },
    { cpe_name: "cpe:2.3:o:dell:idrac9_firmware:7.00.00.00:*:*:*:*:*:*:*", title: "Dell iDRAC9 Firmware 7.00.00.00", deprecated: false },
    { cpe_name: "cpe:2.3:o:hp:integrated_lights-out_5_firmware:2.78:*:*:*:*:*:*:*", title: "HPE iLO 5 Firmware 2.78", deprecated: false },
    { cpe_name: "cpe:2.3:o:microsoft:windows_server_2019:-:*:*:*:*:*:*:*", title: "Microsoft Windows Server 2019", deprecated: false },
    { cpe_name: "cpe:2.3:a:vmware:vsphere:8.0:*:*:*:*:*:*:*", title: "VMware vSphere 8.0", deprecated: false },
    { cpe_name: "cpe:2.3:o:cisco:ios_xe:16.3.1:*:*:*:*:*:*:*", title: "Cisco IOS XE 16.3.1", deprecated: true },
  ];
  mock.onGet(`${url.DCK_BACKEND.ASSET_VERSION}/SuggestCpe`).reply(withAuth((config) => {
    const p = config.params || {};
    const words = String(p.keyword || "").toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return [200, []];
    const rows = CPE_DICTIONARY
      .filter((c) => p.includeDeprecated || !c.deprecated)
      .filter((c) => words.every((w) => `${c.title} ${c.cpe_name}`.toLowerCase().includes(w)))
      .slice(0, Number(p.take) || 20);
    return [200, rows];
  }));

  // AssetVersion/GetVulnerabilities(票 29)—— 弱點爬蟲目錄,**只讀**。
  // 形狀抄 8/14 對 testenv 的實測(ADR-0004,不照文件猜):
  //   severity 是**中文**(實回「嚴重」「高」)、source 是組合字(「cisco-csaf+cisa-kev」)、
  //   category 帶括號(「軟體(IOS XE)」);整份 DTO **沒有**「影響幾台設備」的欄位。
  // 同 controller 的 UpdateVersion／ReportFailure 是爬蟲寫回入口,mock 一併不提供 ——
  // 沒有假端點就不會有人「順手」接上去。
  mock.onGet(`${url.DCK_BACKEND.ASSET_VERSION}/GetVulnerabilities`).reply(withAuth((config) => {
    const p = config.params || {};
    const pageIndex = Number(p.pageIndex || 0);
    const pageSize = Number(p.pageSize || 2147483647);
    const kw = String(p.keyword || "").trim().toLowerCase();
    let list = vulns.map((v) => ({
      id: v.id,
      cve_id: v.cveId,
      cvss_base_score: v.cvss,
      severity: v.level, // 實測就是中文,不要為了「看起來像 enum」翻成 Critical
      category: "軟體(IOS XE)",
      is_kev: !!v.isKev,
      kev_date_added_utc: v.isKev ? "2026-07-01T00:00:00Z" : null,
      kev_due_date_utc: v.isKev ? "2026-08-22T00:00:00Z" : null,
      published_on_utc: "2026-06-15T00:00:00Z",
      description: `${v.cveId} 的說明(mock)`,
      cisco_url: `https://sec.cloudapps.cisco.com/security/center/content/${v.cveId}`,
      nvd_url: `https://nvd.nist.gov/vuln/detail/${v.cveId}`,
      cve_org_url: `https://www.cve.org/CVERecord?id=${v.cveId}`,
      kev_url: v.isKev ? "https://www.cisa.gov/known-exploited-vulnerabilities-catalog" : null,
      source: "cisco-csaf+cisa-kev",
      last_scraped_on_utc: "2026-08-16T02:00:00Z",
      created_on_utc: "2026-06-15T00:00:00Z",
      updated_on_utc: "2026-08-16T02:00:00Z",
    }));
    if (kw) list = list.filter((v) => String(v.cve_id).toLowerCase().includes(kw));
    if (p.isKev !== undefined && p.isKev !== "") {
      const want = p.isKev === true || p.isKev === "true";
      list = list.filter((v) => v.is_kev === want);
    }
    return [200, toPageEnvelope(list, pageIndex, pageSize)];
  }));

  // Dashboard 七支 GET（票 24／43）。數字與明細從**同一份**硬體／軟體／弱點／
  // 審核／合約種子推，不要另立看板專用世界（ADR-0001）。
  const truthy = (v) => v === true || v === "true";
  const isPastDate = (d) => d && new Date(d) < new Date();
  const takeOf = (p, fallback) => {
    const n = Number(p.take);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  };
  const hardwareDashboardRows = () => hardwareStoreOf().all().map(decorateHardware).map((raw) => ({
    ...hardwareRowToDto(raw),
    end_of_sale_date: raw.endOfSale || null,
  }));
  const lifecycleFromHw = (r) => ({
    asset_id: r.id,
    asset_type: "Hardware",
    asset_no: r.asset_no,
    asset_name: r.host_name,
    is_key_business: !!r.is_key_business,
    business_category: r.bizType || "",
    room_id: r.room_id,
    room_name: r.room_name,
    rack_id: r.rack_id,
    rack_no: r.rack_no,
    eos_date: r.eos_date,
    eol_date: r.eol_date,
    end_of_sale_date: r.end_of_sale_date,
    is_expired: false,
    risk_level: null,
  });
  const lifecycleFromSw = (s) => ({
    asset_id: s.id,
    asset_type: "Software",
    asset_no: s.code,
    asset_name: s.name,
    is_key_business: !!s.starred,
    business_category: s.bizType || "",
    room_id: 0,
    room_name: null,
    rack_id: 0,
    rack_no: null,
    eos_date: s.eos || null,
    eol_date: s.eol || null,
    end_of_sale_date: null,
    is_expired: false,
    risk_level: null,
  });
  const filterLifecycle = (rows, which, p) => {
    let list = rows.filter((r) => r[which === "eos" ? "eos_date" : "eol_date"]);
    if (truthy(p.onlyKeyBusiness) || p.onlyKeyBusiness === undefined) {
      // EOS／EOL 文件預設 onlyKeyBusiness=true;CVE／KEV 不會走到這裡
      if (p.onlyKeyBusiness === undefined || truthy(p.onlyKeyBusiness)) {
        list = list.filter((r) => r.is_key_business);
      }
    }
    return list.slice(0, takeOf(p, 10)).map((r) => {
      const date = r[which === "eos" ? "eos_date" : "eol_date"];
      return { ...r, is_expired: !!isPastDate(date), risk_level: r.risk_level || (isPastDate(date) ? "嚴重" : "高") };
    });
  };
  // 「無」與 0 不是等級(2026-09-08 fixture 實測);normalizeRiskLevel 會把它們當沒有等級處理
  const SEVERITY_IDS = { 嚴重: 4, 高: 3, 中: 2, 低: 1 };
  const vulnLineFromSeed = (v, i) => ({
    vulnerability_id: v.id || i + 1,
    cve_id: v.cveId,
    cvss_base_score: v.cvss,
    severity_id: SEVERITY_IDS[v.level] || 0,
    severity: v.level,
    is_kev: !!v.isKev,
    kev_date_added: v.isKev ? "2026-07-01T00:00:00" : null,
    published_on: "2026-06-15T00:00:00",
    affected_asset_count: v.affectedCount,
    affected_key_business_asset_count: v.isKev ? 1 : 0,
  });
  // 命中清單自 2026-09-15(票 39)起以後端 DTO 形狀存種子,分數與等級直接取用 ——
  // 原本由 kevLevel 推 cvss_base_score／severity_id,那是前端憑空生等級(ADR-0007 禁止)。
  const softwareVulnLines = () => software.flatMap((s, si) => (s.vulnerabilityHits || []).map((h, hi) => ({
    vulnerability_id: (si + 1) * 100 + hi,
    cve_id: h.cve_id,
    cvss_base_score: h.cvss_base_score,
    severity_id: SEVERITY_IDS[h.severity] || 0,
    severity: h.severity,
    is_kev: !!h.is_kev,
    kev_date_added: h.kev_date_added_utc || null,
    published_on: h.published_on_utc || null,
    affected_asset_count: (s.deployedHardwareIds || []).length || 1,
    affected_key_business_asset_count: s.starred ? 1 : 0,
  })));
  /**
   * 每級的 vulnerability_count＝該級漏洞筆數;affected_asset_count＝該級**資產數**
   * (9/8 實測:嚴重 vulnerability_count 5、affected_asset_count 3 —— 是台數,不是漏洞影響數加總)。
   * `assetLevels` 給每台資產的該類等級,照真後端的語意數台數。
   */
  const severityStats = (lines, assetLevels = []) => ["嚴重", "高", "中", "低"].map((severity, idx) => ({
    severity_id: 4 - idx,
    severity,
    vulnerability_count: lines.filter((l) => l.severity === severity).length,
    affected_asset_count: assetLevels.filter((lv) => lv === severity).length,
  }));
  const pendingApprovals = () => listApprovals()
    .filter((a) => a.stage !== "approved" && a.stage !== "rejected");

  mock.onGet(`${url.DCK_BACKEND.DASHBOARD}/GetTodo`).reply(withAuth(() => {
    const pending = pendingApprovals();
    return [200, {
      pending_approval_count: pending.length,
      my_initiated_pending_count: pending.filter((a) => String(a.initiator || "").includes("張家豪")).length,
      unread_message_count: notifications.filter((n) => !n.read).length,
    }];
  }));
  mock.onGet(`${url.DCK_BACKEND.DASHBOARD}/GetTodoItems`).reply(withAuth((config) => {
    const p = config.params || {};
    const take = takeOf(p, 10);
    const days = Number(p.expiringDays) || 90;
    const horizon = Date.now() + days * 86400000;
    const approvals = pendingApprovals().map((a) => ({
      item_type: "Approval",
      type_name: "審核",
      reference_no: a.docNo || `RQ-${a.id}`,
      content: a.summary || "",
      occurred_on: a.createdAt,
      due_on: null,
      remaining_days: null,
      source_id: a.id,
      entity_type_name: a.resource || "Approval",
      entity_id: a.recordId || a.id,
    }));
    const expiries = contracts
      .filter((c) => c.endDate && new Date(c.endDate).getTime() <= horizon)
      .map((c) => {
        const remaining = Math.ceil((new Date(c.endDate) - Date.now()) / 86400000);
        return {
          item_type: "Expiry",
          type_name: "到期",
          reference_no: c.name,
          content: `${c.name}（${c.vendorName || "—"}）即將到期`,
          occurred_on: c.endDate,
          due_on: c.endDate,
          remaining_days: remaining,
          source_id: c.id,
          entity_type_name: "Contract",
          entity_id: c.id,
        };
      });
    return [200, [...approvals, ...expiries]
      .sort((a, b) => new Date(b.occurred_on) - new Date(a.occurred_on))
      .slice(0, take)];
  }));
  mock.onGet(`${url.DCK_BACKEND.DASHBOARD}/GetAssetRiskSummary`).reply(withAuth((config) => {
    const assetType = config.params?.assetType || "Hardware";
    if (assetType === "Software") {
      const cveLines = softwareVulnLines();
      const kevLines = cveLines.filter((l) => l.is_kev);
      return [200, {
        asset_type: "Software",
        total_asset_count: software.length,
        eos_declared_count: software.filter((s) => s.eos).length,
        eos_expired_count: software.filter((s) => isPastDate(s.eos)).length,
        eos_unpublished_count: software.filter((s) => !s.eos).length,
        eol_declared_count: software.filter((s) => s.eol).length,
        eol_expired_count: software.filter((s) => isPastDate(s.eol)).length,
        eol_unpublished_count: software.filter((s) => !s.eol).length,
        cve_count: new Set(cveLines.map((l) => l.cve_id)).size,
        cve_affected_asset_count: software.filter((s) => (s.vulnerabilityHits || []).length).length,
        cve_severity_stats: severityStats(cveLines, software.map((s) => s.cveLevel)),
        kev_count: new Set(kevLines.map((l) => l.cve_id)).size,
        kev_affected_asset_count: software.filter((s) => s.kevLevel).length,
        kev_severity_stats: severityStats(kevLines, software.map((s) => s.kevLevel)),
      }];
    }
    const rows = hardwareDashboardRows();
    const cveLines = vulns.map(vulnLineFromSeed);
    const kevLines = cveLines.filter((l) => l.is_kev);
    return [200, {
      asset_type: "Hardware",
      total_asset_count: rows.length,
      eos_declared_count: rows.filter((r) => r.eos_date).length,
      eos_expired_count: rows.filter((r) => isPastDate(r.eos_date)).length,
      eos_unpublished_count: rows.filter((r) => !r.eos_date).length,
      eol_declared_count: rows.filter((r) => r.eol_date).length,
      eol_expired_count: rows.filter((r) => isPastDate(r.eol_date)).length,
      eol_unpublished_count: rows.filter((r) => !r.eol_date).length,
      cve_count: cveLines.length,
      cve_affected_asset_count: rows.filter((r) => r.cve_risk_level).length,
      cve_severity_stats: severityStats(cveLines, rows.map((r) => r.cve_risk_level)),
      kev_count: kevLines.length,
      kev_affected_asset_count: rows.filter((r) => r.kev_risk_level).length,
      kev_severity_stats: severityStats(kevLines, rows.map((r) => r.kev_risk_level)),
    }];
  }));
  mock.onGet(`${url.DCK_BACKEND.DASHBOARD}/GetEosDetails`).reply(withAuth((config) => {
    const p = config.params || {};
    const rows = p.assetType === "Software"
      ? software.map(lifecycleFromSw)
      : hardwareDashboardRows().map(lifecycleFromHw);
    return [200, filterLifecycle(rows, "eos", { ...p, onlyKeyBusiness: p.onlyKeyBusiness === undefined ? true : p.onlyKeyBusiness })];
  }));
  mock.onGet(`${url.DCK_BACKEND.DASHBOARD}/GetEolDetails`).reply(withAuth((config) => {
    const p = config.params || {};
    const rows = p.assetType === "Software"
      ? software.map(lifecycleFromSw)
      : hardwareDashboardRows().map(lifecycleFromHw);
    return [200, filterLifecycle(rows, "eol", { ...p, onlyKeyBusiness: p.onlyKeyBusiness === undefined ? true : p.onlyKeyBusiness })];
  }));
  mock.onGet(`${url.DCK_BACKEND.DASHBOARD}/GetCveDetails`).reply(withAuth((config) => {
    const p = config.params || {};
    const lines = p.assetType === "Software" ? softwareVulnLines() : vulns.map(vulnLineFromSeed);
    return [200, lines.slice(0, takeOf(p, 10))];
  }));
  mock.onGet(`${url.DCK_BACKEND.DASHBOARD}/GetKevDetails`).reply(withAuth((config) => {
    const p = config.params || {};
    const lines = (p.assetType === "Software" ? softwareVulnLines() : vulns.map(vulnLineFromSeed))
      .filter((l) => l.is_kev);
    return [200, lines.slice(0, takeOf(p, 10))];
  }));

  // AdminMenu/GetMenu:依角色動態篩選(ADR-0003;真後端由 token 識別,mock 以登入身分代替)。
  // 結構鏡射 8/11 真後端實回:system_name 用真名(PeopleManagement 等,前端鍵的
  // 對照在 query/useAdminMenu 的 SYSTEM_ALIASES);人員/營運/資產/監控/訊息/決策
  // 為巢狀 child_nodes。注意真選單「沒有審核中心項」(前端功能;已列後端待確認)。
  const node = (system_name, title, child_nodes = []) => ({
    system_name, title, url: null, icon_class: "", visible: true,
    open_url_in_new_tab: false, child_nodes,
  });
  const ADMIN_MENU_ITEMS = [
    node("Dashboard", "智慧看板"),
    node("PeopleManagement", "人員管理", [
      node("PermissionSetting", "權限設定"), node("UserInfo", "使用者資訊"), node("WorkManagement", "工作管理"),
    ]),
    node("RoomManagement", "機房管理"),
    node("OperationInfo", "營運資訊", [
      node("IpInfo", "IP 資訊"), node("ProjectInfo", "專案資訊"),
      node("ContractInfo", "合約資訊"), node("VendorInfo", "廠商資訊"),
    ]),
    node("AssetManagement", "資產管理", [
      node("RackInfo", "機櫃資訊"), node("HardwareInfo", "硬體資訊"), node("SoftwareInfo", "軟體資訊"),
    ]),
    node("MonitoringDispatch", "監控派工", [node("Monitoring", "監控"), node("Dispatch", "派工")]),
    node("MessageCenter", "訊息功能", [node("MessageSetting", "各類訊息設定"), node("MessageQuery", "訊息查詢")]),
    node("Reporting", "報表功能"),
    node("DecisionAnalysis", "決策分析", [node("AlertPrediction", "告警預測"), node("FinancialForecast", "財務預測")]),
  ];
  // 對齊 RbacContext 靜態能力矩陣:Viewer 隱藏 監控/訊息/決策;Wrangler 隱藏 決策
  const MENU_HIDDEN_BY_ROLE = {
    Viewer: ["MessageCenter", "MonitoringDispatch", "DecisionAnalysis"],
    Wrangler: ["DecisionAnalysis"],
  };

  mock.onGet(`${url.DCK_BACKEND.ADMIN_MENU}/GetMenu`).reply(withAuth((config) => {
    let role = "Overseer";
    try {
      const raw = authUserRaw();
      if (raw) role = JSON.parse(raw).role || role;
    } catch (e) { /* ignore */ }
    const hidden = MENU_HIDDEN_BY_ROLE[role] || [];
    return [200, ADMIN_MENU_ITEMS.filter((m) => !hidden.includes(m.system_name))];
  }));

  // —— DcCustomer(DCK-01-02):與 /api/users 共用同一個 user store ——
  // 「同一份資料、兩種契約視角」:審核引擎(patchTarget)與既有頁面照常運作,
  // /api-backend/* 以投影提供後端契約形狀(mapper 見 db/accounts)。

  // 文件必填:employee_no, name, email, mobile_phone, unit_name, role_id;密碼新增必填
  const validateCustomer = (dto, { isCreate }) => {
    const errors = [];
    if (!dto.employee_no) errors.push("員工編號為必填");
    if (!dto.name) errors.push("姓名為必填");
    if (!dto.email) errors.push("電子信箱為必填");
    if (!dto.mobile_phone) errors.push("行動電話為必填");
    if (!dto.unit_name) errors.push("單位名稱為必填");
    if (!dto.role_id) errors.push("角色為必填");
    if (isCreate && !dto.password) errors.push("密碼為必填");
    return errors;
  };

  mock.onGet(`${url.DCK_BACKEND.CUSTOMER}/GetAll`).reply(withAuth((config) => {
    const p = config.params || {};
    const keyword = String(p.keyword || "").trim();
    const unitName = String(p.unitName || "").trim();
    const jobTitle = String(p.jobTitle || "").trim();
    const customerRoleId = Number(p.customerRoleId) || 0;
    const pageIndex = Number(p.pageIndex) || 0;
    const pageSize = Number(p.pageSize) || 20;

    let list = userStore().all().map(userRowToDcCustomerListItem);
    if (keyword) {
      list = list.filter((c) =>
        [c.employee_no, c.name, c.email].some((v) => (v || "").includes(keyword))
      );
    }
    if (unitName) list = list.filter((c) => (c.unit_name || "").includes(unitName));
    if (jobTitle) list = list.filter((c) => (c.job_title || "").includes(jobTitle));
    if (customerRoleId) list = list.filter((c) => c.role_id === customerRoleId);
    if (p.isActive !== undefined && p.isActive !== null && p.isActive !== "") {
      const isActive = p.isActive === true || p.isActive === "true";
      list = list.filter((c) => c.active === isActive);
    }
    return [200, toPageEnvelope(list, pageIndex, pageSize)];
  }));

  mock.onGet(new RegExp(`${url.DCK_BACKEND.CUSTOMER}/GetById/\\d+$`)).reply(withAuth((config) => {
    const id = Number(config.url.split("/").pop());
    const found = userStore().find(id);
    if (!found) {
      // 文件:DcCustomer 的 404 body 為 string(非 ProblemDetails;前端 404 一律制式文案)
      return [404, "查無使用者資訊"];
    }
    return [200, userRowToDcCustomer(found)];
  }));

  // —— 密碼流程(ticket 09,文件 8/11;掛在 DcCustomer controller 下)——
  // GET ChangePassword:模型含首登改密/密碼逾期旗標(身分組裝的資料源)
  mock.onGet(`${url.DCK_BACKEND.CUSTOMER}/ChangePassword`).reply(withAuth((config) => {
    const me = currentUserRow();
    if (!me) return [400, "無法識別當前使用者"]; // 文件:GET 的 400 為 string
    return [200, {
      old_password: "", new_password: "", confirm_new_password: "",
      password_must_be_changed: !!me.mustChangePassword,
      password_expired: false,
      custom_properties: {},
    }];
  }));

  // POST ChangePassword:驗舊密碼+規則;400 為 string[](逐欄)
  mock.onPost(`${url.DCK_BACKEND.CUSTOMER}/ChangePassword`).reply(withAuth((config) => {
    const me = currentUserRow();
    if (!me) return [400, ["無法識別當前使用者"]];
    const dto = JSON.parse(config.data || "{}");
    const errors = [];
    if (!String(dto.old_password || "")) errors.push("請輸入舊密碼。");
    else if (!credentialsValid(me.id, dto.old_password)) errors.push("舊密碼不正確。");
    const issue = passwordIssue(dto.new_password);
    if (issue) errors.push(issue);
    if (dto.new_password !== dto.confirm_new_password) errors.push("兩次輸入的新密碼不一致。");
    if (!issue && dto.new_password && dto.new_password === dto.old_password) {
      errors.push("新密碼不可與舊密碼相同。");
    }
    if (errors.length) return [400, errors];
    setInitialPassword(me.id, dto.new_password);
    userStore().patch(me.id, { mustChangePassword: false }); // 首登阻擋解除
    return [200, {
      old_password: "", new_password: "", confirm_new_password: "",
      password_must_be_changed: false, password_expired: false, custom_properties: {},
    }];
  }));

  // GET PasswordRecovery:忘記密碼模型。登入前流程,不驗 Bearer
  // (文件對全端點標 401,唯 recovery 語意上必須匿名可用 —— 待真後端實測確認)
  mock.onGet(`${url.DCK_BACKEND.CUSTOMER}/PasswordRecovery`).reply(() => [200, {
    email: "", result: "", display_captcha: false, custom_properties: {},
  }]);

  // POST PasswordRecoverySend:200 + result。2026-09-04 實測沒註冊是
  // 「未找到電子信箱」(不是防列舉的同一句「已寄出」);有註冊才發 token。
  // mock 的 dev_token 只給測試用,真後端不回、改寄信。
  mock.onPost(`${url.DCK_BACKEND.CUSTOMER}/PasswordRecoverySend`).reply((config) => {
    const dto = JSON.parse(config.data || "{}");
    const key = String(dto.email || "").trim().toLowerCase();
    const hit = userStore().all().find((u) => (u.email || "").toLowerCase() === key);
    if (!hit) {
      return [200, {
        email: dto.email || "",
        result: "未找到電子信箱",
        display_captcha: false,
        custom_properties: {},
      }];
    }
    return [200, {
      email: dto.email || "",
      result: "若該 Email 已註冊，重設連結已寄出（30 分鐘內有效），請至信箱查收。",
      display_captcha: false,
      custom_properties: { dev_token: issueRecoveryToken(hit.email) },
    }];
  });

  // Confirm 兩式共用:token 有效+customerGuid 對得上才放行(信箱連結的兩把鑰匙)
  const recoveryTarget = (config) => {
    const q = new URLSearchParams(config.url.split("?")[1] || "");
    const token = q.get("token") || "";
    const guid = q.get("customerGuid") || "";
    const state = recoveryTokenState(token);
    if (!state.valid) return null;
    const hit = userStore().all().find(
      (u) => (u.email || "").toLowerCase() === String(state.email).toLowerCase()
    );
    if (!hit || mockGuidOf(hit.id) !== guid) return null;
    return { token, user: hit };
  };

  // POST PasswordRecoveryConfirmPOST:提交新密碼(注意先註冊,路徑是 Confirm 的前綴延伸)
  mock.onPost(new RegExp(`${url.DCK_BACKEND.CUSTOMER}/PasswordRecoveryConfirmPOST(\\?|$)`)).reply((config) => {
    const t = recoveryTarget(config);
    if (!t) return [404, "重設連結無效或已過期"];
    const dto = JSON.parse(config.data || "{}");
    const errors = [];
    const issue = passwordIssue(dto.new_password);
    if (issue) errors.push(issue);
    if (dto.new_password !== dto.confirm_new_password) errors.push("兩次輸入的新密碼不一致。");
    if (errors.length) return [400, errors];
    setInitialPassword(t.user.id, dto.new_password);
    consumeRecoveryToken(t.token); // 一次性
    userStore().patch(t.user.id, { mustChangePassword: false });
    return [200, {
      new_password: "", confirm_new_password: "", disable_password_changing: false,
      result: "密碼已重設,請以新密碼登入。", return_url: "/auth-login", custom_properties: {},
    }];
  });

  // POST PasswordRecoveryConfirm:讀模型(=驗證連結,未提交)
  mock.onPost(new RegExp(`${url.DCK_BACKEND.CUSTOMER}/PasswordRecoveryConfirm(\\?|$)`)).reply((config) => {
    const t = recoveryTarget(config);
    if (!t) return [404, "重設連結無效或已過期"];
    return [200, {
      new_password: "", confirm_new_password: "", disable_password_changing: false,
      result: "", return_url: "", custom_properties: {},
    }];
  });

  mock.onPost(`${url.DCK_BACKEND.CUSTOMER}/Create`).reply(withAuth((config) => {
    const dto = JSON.parse(config.data || "{}");
    const errors = validateCustomer(dto, { isCreate: true });
    if (userStore().all().some((r) => r.employeeId === dto.employee_no)) {
      errors.push("員工編號已存在");
    }
    if (errors.length) return [400, errors];
    // 新增先建立資料列(狀態=審核中),後續由審核單核准(與 REST create 行為一致);
    // 初始密碼交給 auth 側保存、帳號標記首登須改密(同 REST beforeCreate 規則)
    const row = dcCustomerPatchToUserRow(dto);
    const record = userStore().add({ status: "啟用中", ...row, approvalStatus: "pending" });
    setInitialPassword(record.id, dto.password);
    userStore().patch(record.id, { mustChangePassword: true });
    return [200, userRowToDcCustomer(userStore().find(record.id))];
  }));

  mock.onPut(`${url.DCK_BACKEND.CUSTOMER}/Update`).reply(withAuth((config) => {
    const dto = JSON.parse(config.data || "{}");
    const found = userStore().find(dto.id);
    if (!found) {
      return [404, "查無使用者資訊"];
    }
    const errors = validateCustomer(dto, { isCreate: false });
    if (errors.length) return [400, errors];
    const patched = userStore().patch(dto.id, dcCustomerPatchToUserRow(dto));
    return [200, userRowToDcCustomer(patched)];
  }));

  mock.onDelete(new RegExp(`${url.DCK_BACKEND.CUSTOMER}/Delete/\\d+$`)).reply(withAuth((config) => {
    const id = Number(config.url.split("/").pop());
    const found = userStore().find(id);
    if (!found) {
      return [404, "查無使用者資訊"];
    }
    // 刪除阻擋規則與 REST 端共用(registerResource 的 canDelete:名下負責工作明細)
    const blocked = userStore().blockedReason(id);
    if (blocked) return [409, { message: blocked }];
    userStore().remove(id);
    return [200];
  }));

  // ⚠️ 批次匯入:Backend v1.00 未提供 DcCustomer 匯入端點,此為前端預留的 mock 版
  mock.onPost(`${url.DCK_BACKEND.CUSTOMER}/Import`).reply(withAuth((config) => {
    const payload = JSON.parse(config.data || "{}");
    const rows = Array.isArray(payload.rows) ? payload.rows : [];
    const failed = [];
    let success = 0, replaced = 0;
    rows.forEach((row, index) => {
      const missing = ["employee_no", "name", "email"].filter((f) => !row[f]);
      if (missing.length) {
        missing.forEach((f) => failed.push({ index, field: f, reason: `「${f}」為必填` }));
        return;
      }
      const mapped = dcCustomerPatchToUserRow(row);
      const existing = userStore().all().find((r) => r.employeeId === row.employee_no);
      if (existing) {
        userStore().patch(existing.id, { ...mapped, approvalStatus: "pending" });
        replaced += 1;
      } else {
        userStore().add({ status: "啟用中", ...mapped, approvalStatus: "pending" });
        success += 1;
      }
    });
    return [200, { total: rows.length, success, replaced, failedCount: failed.length, failed }];
  }));

  // —— 批次匯入:DC Keeper 標準範本(票 dckeeper-standard-import-20260909/01)——
  // 真後端 DcImport/ImportDcKeeper* 收 multipart `file`、回 ImportResultDto。mock 吃同一種請求:
  // 把 xlsx 讀出來、**依欄序**對欄位(swagger 寫的是「欄序：…」,後端不看標題文字 —— 舊版範本上傳
  // 會照樣錯位,mock 不幫它對齊,否則全 mock 模式綠、上 SIT 才爆),寫進各 store,讓全 mock 模式匯完清單會長出來。
  // 值語意照後端描述:inserted/updated 以主鍵(資產編號)比對;errors 該列不匯入;warnings 該列仍匯入。
  const readFormFile = (req) => new Promise((resolve, reject) => {
    const fd = req.data;
    const file = fd && typeof fd.get === "function" ? fd.get("file") : null;
    if (!file) { reject(new Error("缺少 file")); return; }
    const reader = new FileReader();
    reader.onload = (e) => resolve(new Uint8Array(e.target.result));
    reader.onerror = () => reject(new Error("read"));
    reader.readAsArrayBuffer(file);
  });
  const templateRows = (bytes, columns) => {
    const wb = XLSX.read(bytes, { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    // SheetJS 對亂碼也會「解析」成空表,不會丟錯 —— 看**標題列**有沒有該範本的第一欄來判斷是不是那份範本
    // (機房/機櫃是「機房名稱」、軟硬體是「資產編號」)。只看標題列、不看有沒有資料列:
    // 剛下載的空模板直接上傳是合法的「0 筆」(真後端也這樣回,smoke 的寫入模式就是用這招),不是格式不符。
    const firstLabel = columns[0].label;
    const [header = [], ...body] = ws ? XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", blankrows: false }) : [];
    if (String(header[0] ?? "").trim() !== firstLabel) {
      throw new Error("not-template");
    }
    return body.map((cells) => Object.fromEntries(
      columns.map((c, i) => [c.key, typeof cells[i] === "string" ? cells[i].trim() : cells[i] ?? ""])
    ));
  };
  // 日期欄:Excel 的日期格讀出來是序號(46568),照字串存會被當成西元 46568 年 → 風險等級算不出來。
  // 序號用 SheetJS 的 SSF 拆年月日(不經 Date,免時區位移);文字日期(2028/12/31)統一成 YYYY-MM-DD。
  const importDate = (v) => {
    if (v === "" || v == null) return "";
    const pad = (n) => String(n).padStart(2, "0");
    if (typeof v === "number") {
      const d = XLSX.SSF.parse_date_code(v);
      return d ? `${d.y}-${pad(d.m)}-${pad(d.d)}` : "";
    }
    const m = String(v).trim().match(/^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})$/);
    return m ? `${m[1]}-${pad(m[2])}-${pad(m[3])}` : String(v).trim();
  };
  const importResult = (rows, apply) => {
    const errors = []; const warnings = [];
    let inserted = 0; let updated = 0;
    rows.forEach((r, i) => {
      const rowNumber = i + 2; // 標題列是第 1 列
      const out = apply(r, (msg) => warnings.push({ row_number: rowNumber, message: msg }));
      if (out === "error") errors.push({ row_number: rowNumber, message: "資產編號為必填" });
      else if (out === "updated") updated += 1;
      else inserted += 1;
    });
    return {
      total_rows: rows.length, success_count: inserted + updated, inserted_count: inserted,
      updated_count: updated, failure_count: errors.length, errors, warnings,
    };
  };
  // 資產編號可能一格多號(樣本:「105-000635\r\n105-000637」)——取第一個、警告其餘
  const firstAssetNo = (raw, warn) => {
    const parts = String(raw || "").split(/[\r\n\s]+/).filter(Boolean);
    if (parts.length > 1) warn(`資產編號有多個(${parts.slice(1).join("、")}),僅以第一個「${parts[0]}」匯入`);
    return parts[0] || "";
  };
  // swagger:400 的 body 是 string(不是 {message});前端 upload 請求標了 trustStringError 才會採用
  const importHandler = (columns, apply) => withAuth((req) => readFormFile(req)
    .then((bytes) => [200, importResult(templateRows(bytes, columns), apply)])
    .catch(() => [400, `檔案格式不符:找不到「${columns[0].label}」標題列,請確認為 DC Keeper 標準範本 .xlsx。`]));
  // 同編號(硬體)／同編號＋名稱(軟體)→ 更新,否則新增;回值給 importResult 記數
  const upsert = (store, match, mapped, defaults) => {
    const existing = store.all().find(match);
    if (existing) { store.patch(existing.id, mapped); return "updated"; }
    store.add({ ...defaults, ...mapped });
    return "inserted";
  };
  const keyBusiness = (v) => /^y$/i.test(String(v || ""));
  // 範本的數字欄可能是空字串或全形空白;非數字一律當 0(真後端同樣不會因為空格擋整列)
  const numOf = (v) => (Number.isFinite(Number(v)) && String(v).trim() !== "" ? Number(v) : 0);
  // 機房:主鍵＝機房名稱。樓層／區域在 mock 是掛在機房上的巢狀結構,匯入只落機房本身,
  // 樓層／區域交給 createMissingReferences(真後端行為),mock 只把值記在 row 上供檢視。
  mock.onPost(`${url.DCK_BACKEND.IMPORT}/ImportDcKeeperRooms`).reply(importHandler(DCKEEPER_ROOM_COLUMNS, (r) => {
    const name = String(r.room_name || "").trim();
    if (!name) return "error";
    const mapped = { name, address: r.address || "", managerName: r.manager || "" };
    return upsert(roomStore(), (x) => x.name === name, mapped, { nameEn: name, code: name, city: "", floors: [] });
  }));
  // 機櫃:主鍵＝機櫃編號。機房以名稱查,查不到就照 createMissingReferences 的語意留著名稱字串。
  mock.onPost(`${url.DCK_BACKEND.IMPORT}/ImportDcKeeperRacks`).reply(importHandler(DCKEEPER_RACK_COLUMNS, (r, warn) => {
    const code = String(r.rack_no || "").trim();
    if (!code) return "error";
    const room = roomStore().all().find((x) => x.name === String(r.room_name || "").trim());
    if (!room) warn(`查無機房「${r.room_name || ""}」,已依設定自動建檔`);
    const mapped = {
      code, name: r.asset_name || code, roomId: room ? room.id : null,
      floor: r.floor || "", area: r.area || "", bizType: r.business_category || "",
      starred: keyBusiness(r.key_business), assetType: r.asset_category || "",
      propertyNo: r.property_no || "", brand: r.brand || "", model: r.model || "",
      powerCircuit: r.power_circuit || "", voltage: numOf(r.voltage), capacityKva: numOf(r.capacity_kva),
      leftPowerCount: numOf(r.left_power_count), rightPowerCount: numOf(r.right_power_count),
      pduCount: numOf(r.pdu_count), uTotal: numOf(r.total_u),
      mountDate: importDate(r.mounted_date), managerName: r.manager || "",
      status: "啟用中", approvalStatus: "approved",
    };
    return upsert(cabinetStoreOf(), (x) => x.code === code, mapped, { uUsed: 0, pduUsed: 0 });
  }));

  mock.onPost(`${url.DCK_BACKEND.IMPORT}/ImportDcKeeperHardware`).reply(importHandler(DCKEEPER_HARDWARE_COLUMNS, (r, warn) => {
    const code = firstAssetNo(r.asset_no, warn);
    if (!code) return "error";
    // 機櫃以編號對既有機櫃(機房由機櫃帶出,清單的 room_name／rack_no 都從 cabinetId 推);
    // 查無就照機櫃匯入那條同樣的語意:給 warning、該列照樣匯入,只是沒有位置。
    const rackNo = String(r.rack_no ?? "").trim();
    const rack = rackNo ? cabinetStoreOf().all().find((x) => x.code === rackNo) : null;
    if (rackNo && !rack) warn(`查無機櫃「${rackNo}」,已依設定自動建檔`);
    const u = parseUPosition(r.position_u);
    const uRaw = String(r.position_u ?? "").trim();
    if (uRaw && u.start === null) warn(`U位「${uRaw}」看不懂(例:U12 或 U12-13),未寫入位置`);
    const mapped = {
      code, name: r.host_name || code, brand: r.brand || "", model: r.model || "", serialNo: String(r.serial_no ?? ""),
      firmwareVersion: String(r.firmware_version ?? ""), ip: String(r.ip_address || "").split(";")[0].trim(),
      starred: keyBusiness(r.key_business), status: "運行中", lifecycle: "commissioned", approvalStatus: "approved",
      ...(rack ? { cabinetId: rack.id } : {}),
      ...(u.start !== null ? { uPosition: String(u.start), uSize: u.end - u.start + 1 } : {}),
      eos: importDate(r.eos_date), eol: importDate(r.eol_date),
      managerName: r.manager || "",
    };
    return upsert(hardwareStoreOf(), (x) => x.code === code, mapped, { assetType: "系統相關", bizType: "一類業務", uSize: 1 });
  }));
  mock.onPost(`${url.DCK_BACKEND.IMPORT}/ImportDcKeeperSoftware`).reply(importHandler(DCKEEPER_SOFTWARE_COLUMNS, (r, warn) => {
    const code = firstAssetNo(r.asset_no, warn);
    if (!code) return "error";
    const name = r.software_name || r.installed_host_name || code;
    const mapped = {
      code, name, version: String(r.version || ""), managerName: r.manager || "",
      starred: keyBusiness(r.key_business), status: "運行中", approvalStatus: "approved",
    };
    // 軟體主鍵:資產編號＋軟體名稱(同一台主機會有很多套軟體)
    return upsert(softwareStoreOf(), (x) => x.code === code && x.name === name, mapped, { bizType: "一類業務", assetType: "軟體相關", env: "正式" });
  }));

  // 共用：三關審核引擎（app 帶種子,讓審核中心一進來就有可檢視單據）
  registerAuditArchive(mock);
  // 已廢除示例(WF_v3 票 21):一間刪除核准後進保留區的機房,
  // 「顯示已廢除資料」開關才看得到東西。不在 rooms 種子裡(它已經被刪了)。
  seedArchiveEntry({
    resource: "room",
    deletedBy: "林承翰（EMP-0031）",
    deletedAt: "2026-07-20T10:00:00+08:00",
    record: {
      id: 901,
      name: "高雄前鎮機房",
      nameEn: "KHH-QJ DC",
      code: "KHH-QJ",
      city: "高雄市",
      address: "高雄市前鎮區成功二路 88 號",
      managerId: 4,
      manager: "林承翰",
      floors: [{ id: 9011, name: "一樓", areas: [{ id: 90111, name: "A區" }] }],
      cabinetCount: 0,
      status: "停用",
      approvalStatus: "approved",
      changeLog: [],
    },
  });
  // 同批已廢除示例:機櫃/硬體/軟體各一(WF_v3 票 21;shape 同各自的種子列)
  seedArchiveEntry({
    resource: "cabinet",
    deletedBy: "陳雅婷（EMP0113）",
    deletedAt: "2026-08-01T09:00:00+08:00",
    record: {
      id: 902, code: "R-Z09", name: "Z09 機櫃(汰換)", roomId: 1, floorId: 1, areaId: 11,
      keyBiz: "不啟用", assetCode: "AST-2023-0099", propertyNo: "", purchaseAmount: 150000,
      bizType: "三類業務", assetType: "機房相關", brand: "APC", model: "AR2900",
      powerCircuit: "PC-09", voltage: "220V", capacityKva: "3", uTotal: 24,
      powerCount: 2, leftPower: 1, rightPower: 1, pduCount: 2, pduUsed: 0,
      mountDate: "2023-01-10", amortEndDate: "2026-01-10", docNo: "",
      manager: "林承翰", status: "停用", approvalStatus: "approved", changeLog: [],
    },
  });
  seedArchiveEntry({
    resource: "hardware",
    deletedBy: "林承翰（EMP0145）",
    deletedAt: "2026-08-05T14:00:00+08:00",
    record: {
      id: 903, code: "SRV-Z09-001", name: "汰換備援主機", assetType: "系統相關",
      bizType: "三類業務", brand: "Dell", model: "PowerEdge R640", serialNo: "OLD9Y4L1",
      cabinetId: null, uPosition: null, uSize: 1, ip: "10.10.39.9",
      eol: "2025-12-31", eos: "2026-06-30", isSpare: false, status: "停用",
      amortEndDate: "2026-06-30", manager: "林承翰", approvalStatus: "approved",
    },
  });
  seedArchiveEntry({
    resource: "software",
    deletedBy: "林承翰（EMP0145）",
    deletedAt: "2026-08-05T14:10:00+08:00",
    record: {
      id: 904, code: "SW-0099", name: "舊版備份系統", brand: "Veritas",
      model: "NetBackup", version: "8.1", deployedHardwareIds: [],
      eos: "2024-12-31", eol: "2025-12-31", cveLevel: null, kevLevel: null, vulnerabilityHits: [],
      manager: "林承翰", managerId: 4, starred: false, bizType: "三類業務",
      assetType: "軟體相關", env: "營運", propertyNo: "", serialNo: "NB81-OLD",
      remark: "", scan: { date: "", nextDate: "", rows: [] },
      pentest: { date: "", nextDate: "", rows: [] },
      status: "停用", approvalStatus: "approved",
    },
  });
  // 票 29:角色/使用者也各一筆已廢除示例(裁示 9/13:已廢除＝稽核保留區,與資產四頁一致)。
  // 保留區存的是**列表 DTO 整筆**(這兩頁直接畫契約欄位),不是 store 列。
  seedArchiveEntry({
    resource: "role",
    deletedBy: "陳雅婷（EMP0113）",
    deletedAt: "2026-08-12T09:30:00+08:00",
    record: {
      id: 905, name: "專案臨時人員", description: "2026 Q2 專案期間臨時帳號用（唯讀）",
      active: false, is_system_role: false, system_name: "ProjectTemp", role_type_name: "自訂",
      bound_customer_count: 0, last_modified_on: "2026/07/31 17:00",
      last_modified_by_customer_id: null, last_modified_by_name: "陳雅婷",
      is_under_review: false, approval_request_no: null, modules: [], fields: [],
      custom_properties: { scope: "唯讀", change_log: [] },
    },
  });
  seedArchiveEntry({
    resource: "user",
    deletedBy: "林承翰（EMP-0031）",
    deletedAt: "2026-08-15T11:00:00+08:00",
    record: {
      id: 906, username: "meiling.chou", name: "周美玲", employee_no: "EMP-0208",
      unit_name: "資訊處", job_title: "系統工程師", role_id: 3, role_name: "Viewer",
      email: "meiling.chou@example.com", phone: "+886-02-2345-6789", extension: "305",
      mobile_country_code: "+886", mobile_phone: "0922333444",
      active: false, status: "停用", is_under_review: false, approval_request_no: null,
    },
  });
  registerApprovals(mock, { seed: true });
  registerPasswordReset(mock);

  // eslint-disable-next-line no-console
  console.info(
    "%c[DC Keeper] Mock 層已啟用",
    "color:#0aa",
    "— 設 REACT_APP_USE_MOCK=false 可停用"
  );

  const origRestore = mock.restore.bind(mock);
  mock.restore = () => {
    origRestore();
    if (liveAdapter === mock) liveAdapter = null;
  };

  return mock;
}
